import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { apiGet } from '../../utils/api';
import { BTN_XS } from '../../constants/buttons';
import {
  GAG_STATUSES,
  GAG_STATUS_LABELS,
  GAG_STATUS_COLORS_HEX,
  TASK_PRIORITY_LABELS,
  TASK_STATUS_COLORS_HEX,
} from '../../constants/taskStatuses';
import { GAG_RA_BADGES } from '../../constants/colors';
import { gagStatusTabCounts } from '../../constants/gagBreakdown';
import { formatDate } from '../../utils/dateFormat';
import useDragScroll from '../../hooks/useDragScroll';
import useGanttZoom from '../../hooks/useGanttZoom';
import { parseDate, daysDiff, addDays, toDateStr, buildDateHeaders } from '../../utils/ganttDates';
import LoadingSpinner from '../LoadingSpinner';
import Dropdown from '../ui/Dropdown';
import ErrorMessage from '../ErrorMessage';
import EmptyState from '../EmptyState';
import GanttDateAxis, { GANTT_HEADER_HEIGHT, GANTT_AXIS_Z, GANTT_DRAG_Z, GANTT_BAR_HEIGHT, GanttGrid, GanttTodayLine } from '../gantt/GanttDateAxis';
import GanttZoomControl from '../gantt/GanttZoomControl';
import GanttMilestoneLegend, { useMilestoneVisibility } from '../gantt/GanttMilestoneLegend';
import { GANTT_CURSOR, GANTT_CURSOR_CLASS } from '../../constants/ganttCursors';
import {
  useGanttMilestoneDrag, useGanttTaskDrag, ganttBarSpan, ganttTaskDragZones,
  MilestoneShape, MILESTONE_SHAPE_CLASSES, isUnassignedTask, unassignedBarStyle, UNASSIGNED_RING, needsOwner,
} from '../teams/CrossTeamGanttView';
import { MILESTONE_DEFS } from './GagGanttView';
import { ganttGagDateLiveKey } from '../gantt/ganttLiveDrag';
import GanttDragDatesTooltip, { GANTT_DRAGGING_ATTR } from '../gantt/GanttDragDatesTooltip';
import { rescheduleGagTask, moveGagDate, gagTaskDragAllowed } from './gagTaskPlanning';
import { ExpandChevron } from '../TableRowExpansion';
import { useToastOrNoop } from '../../contexts/ToastContext';

// Department Timeline view (Task 4.2) — one row per live gag, in shoot order
// or grouped by sub-department. STANDALONE on purpose: CrossTeamGanttView's
// milestone lane is a single strip (not per-row), its bars are coloured per
// GROUP not per row, and it drops rows that have no start/due dates. This
// component reuses the shared axis / zoom / today-marker / pan-scroll pieces
// and — for every drag — CrossTeamGanttView's exported drag hooks.
//
// Data: fetches /api/gags/timeline itself (dept-scoped; refetches on dept
// change and when refreshToken bumps). Windows are server-derived
// (access/prep/strike + live child task dates; shoot never folds in), so the
// gag's own bar is NOT draggable — owner 2026-10-11: "The gant chart shows the
// RESULT of the task planning and doesnt DRIVE the planning."
//
// 🔀 EXPAND A GAG (2026-10-12, owner: "Should task planning actual openup in
// the original time line like when we expand a gannt bar?" … "I would want the
// ability to drag the tasks around including milestones"). Each gag row has an
// expand chevron. Open, its child rows sit directly beneath it on the SAME
// date axis: a Milestones lane (access / prep / strike — shoot stays the
// diamond on the gag's own row) and one row per DATED task, each draggable
// exactly as in the gag panel's task Gantt: a task moves or resizes by either
// edge, a milestone moves; every move asks to confirm, then saves through the
// panel's own two requests (`gagTaskPlanning.js`). The parent bar follows,
// because the server derives it from those dates and the rows are re-read.
//   • Several gags may be open. Expanded state is SESSION state (this page
//     persists no view state). Collapsing keeps the loaded tasks, so
//     re-expanding costs no request; one `GET /api/gags/:id` per gag opened.
//   • AUTHORITY IS THE SERVER'S: a milestone drags when the timeline row's
//     `can_edit` is true; a task drags when the gag DETAIL's `can_edit_tasks`
//     is (`gagTaskDragAllowed`, the gag panel Gantt's own gate). Nothing is
//     re-derived here, and a marker or bar the viewer may not move carries no
//     drag affordance at all.
//   • Undated tasks are COUNTED on the open gag ("2 not dated"), never drawn
//     and never dated by a drag; the gag's panel is where they get dates.
//   • A click on the gag's name or bar opens these rows AND the inline panel
//     (which keeps its own Task planning section); the chevron does the same.
//
// Props:
//  - filters: `{ q, statuses }` — the page's search text (matches TITLE only
//    here; see GagBreakdown's timeline-mode note) and its active status tab
//    (`[]` for All), applied CLIENT-side. 🔻 Sub-department, priority, owner,
//    shoot-range and RA-required went with the page's `Filters ▾` (2026-10-02).
//  - userId: the viewer, for the shared task-edit predicate only (the gates
//    themselves are the server's flags). `canManage` is accepted and NOT read.
//  - activeDeptId: explicit department scope (matters for global admins)
//  - refreshToken: bump to force a silent refetch (detail-panel saves); the
//    open gags' tasks are re-read with it
//  - selectedGagId: highlight the row whose panel is open below
//  - onOpenGag(gag): open the gag's detail panel (page renders it inline)
//  - onChanged(): a date or task move was saved here — the page refetches the
//    list and tells the open panel to re-read

const dateOnly = (v) => (v ? String(v).slice(0, 10) : null);
// Which marker is which, for the shared milestone drag: gag id + date field.
const milestoneKey = (m) => m.key;

// Grouping toggle: 'all' (one continuous row list in shoot order — THE
// DEFAULT) or 'sub_department' (collapsible groups).
//
// 🔑 SESSION STATE, NOT A STORED PREFERENCE — owner ruling 2026-09-22, said
// twice, "every time". `sfx_breakdown_timeline_group` (`TL_GROUP_KEY`) and
// its loader were DELETED with this change, reader and writer together, and
// must not come back. The view opens in shoot order every time; switching
// within a session is unchanged.
//
// 🚨 WHY THE PERSISTENCE HAD TO GO AND NOT JUST THE DEFAULT: a stuck stored
// preference is the reason the owner never saw the default he had already
// been given. Flipping the initial value alone would have left every user
// who had ever touched the toggle — including him — pinned to the old mode
// forever, so the change would have looked like it had not shipped.
//
// 🪤 `npm run check-bloat` CANNOT SEE THIS DELETION IN EITHER DIRECTION, so
// do not read a green run as evidence the key is gone. Its orphan-key scan
// matches `localStorage.setItem` with a STRING LITERAL, and this key was
// always passed as a CONSTANT — the identical blind spot that let
// `sfx_breakdown_view`'s reader be deleted in 2026-08-07 with no warning.
// The literal `sfx_breakdown_timeline_group` was grepped by hand across the
// tree: this file held the only reader and the only writer, and both are
// gone. Grep it by hand again rather than trusting a gate.
//
// 🔻 This mirrors the decision already taken for the TABLE side — the page
// "always opens in table view" and `sfx_breakdown_view` was deleted for the
// same reason. Same ruling, same shape, one fewer stored view preference.
const DEFAULT_GROUP_MODE = 'all';
// The two arrangements the Arrange dropdown offers; 'short' is what its trigger shows.
const ARRANGE_MODES = [
  { id: 'all', label: 'All (shoot order)', short: 'Shoot order' },
  { id: 'sub_department', label: 'By sub-department', short: 'Sub-dept' },
];

/** NULLs-last comparison of two nullable sortable values; 0 when tied. */
function nullsLast(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a < b ? -1 : a > b ? 1 : 0;
}

// ── B2: a gag with NO SHOOT DATE sorts to the TOP ───────────────────────
//
// Owner ruling 2026-09-22: dateless gags must be VISIBLE, "at the top of the
// list", each carrying a "No shoot date" pill where its diamond would be.
//
// 🚨 "NO SHOOT DATE" IS NOT "NO DATES AT ALL", and conflating them is what
// made this hard to see. A gag with no `shoot_date` but with a window
// (access/prep/strike, or a dated child task) has ALWAYS reached this
// component and always rendered — as a bar with no diamond, sorted to the
// BOTTOM by the NULLs-last rule below. Only the genuinely dateless were
// dropped, and they were dropped SERVER-side. Both arrive now; both get the
// pill; the first still draws its bar.
//
// 🔑 ONE predicate, used by BOTH modes — flat order and the within-group
// order — so "dateless first" is a property of the VIEW rather than of one
// toggle position. In grouped mode that means the top of each group, which
// is what "the top of the list" means when there are several lists.
export const hasNoShootDate = (g) => !g.shoot_date;

// Flat-mode order. Below the dateless block it MIRRORS the server's timeline
// ordering semantics (GET /api/gags/timeline): shoot_date NULLs-last, then
// window_start NULLs-last, then title. Re-sorted client-side so optimistic
// shoot-date moves keep the list honest between confirm and refetch.
// 🪤 The dateless test runs FIRST and inverts what `nullsLast` would do with
// the same rows — it is not a refinement of that rule, it overrides it.
export function flatOrder(a, b) {
  const aNone = hasNoShootDate(a);
  const bNone = hasNoShootDate(b);
  if (aNone !== bNone) return aNone ? -1 : 1;
  return nullsLast(a.shoot_date, b.shoot_date)
    || nullsLast(a.window_start, b.window_start)
    || String(a.title || '').localeCompare(String(b.title || ''));
}

// Exported (alongside focusWindow below) so tests derive expected offsets
// from these values rather than re-typing them and risking drift.
export const ROW_HEIGHT = 42;
// 22 since 2026-10-13 (owner: "try the slightly thinner bars"); was 26. Shared.
const BAR_HEIGHT = GANTT_BAR_HEIGHT;
// A collapsed gag's markers sit CENTRED ON THE BAR'S TOP EDGE, clear of the
// title, in their own hues with the white halo the Labour rail uses (owner
// 2026-10-13: "try aligning the milestones to the top of the bars", then "this
// is now looking good"). They were white, centred in the bar, over the title.
const BAR_TOP = (ROW_HEIGHT - BAR_HEIGHT) / 2;
const MARKER_HALO = 'drop-shadow(0 0 1px #fff) drop-shadow(0 0 1px #fff)';
export const GROUP_HEIGHT = 36;
// A gag's EXPANDED child rows (milestones lane, a dated task, a status note).
// Slimmer than the gag's own row so the parent stays the heavier line.
export const CHILD_ROW_HEIGHT = 32;
const CHILD_BAR_HEIGHT = 20;

// ── THE ROW MODEL (2026-10-12: a gag can be EXPANDED) ───────────────────
//
// `flatRows` is ONE flat list of four kinds of row, in render order:
//   { type: 'group', key, label, count }      a sub-department header
//   { type: 'gag', gag }                       a gag's own row (bar + diamond)
//   { type: 'lane' | 'task' | 'note', key, gag, … }
//                                              a CHILD row of the gag above it
// A gag's child rows follow it directly and exist only while it is expanded.
//
// 🔑 `rowHeight` IS THE ONE HEIGHT RULE. Every offset in this file — the
// focus window, the selected row's top, the mount scroll, the grid height —
// is a walk over `flatRows` summing it. Never write `group ? … : …` again:
// that two-way test is exactly what a third row height breaks silently.
// 🪤 A child row CARRIES ITS PARENT `gag`, so "is this a gag row" is
// `isGagRow` (`type === 'gag'`), never `type !== 'group'` and never `r.gag`.
// Pinned by `GagTimelineView.rowModel.test.js`.
export function rowHeight(row) {
  if (row.type === 'group') return GROUP_HEIGHT;
  if (row.type === 'gag') return ROW_HEIGHT;
  return CHILD_ROW_HEIGHT;
}
const isGagRow = (row) => row.type === 'gag';
const isChildRow = (row) => row.type !== 'gag' && row.type !== 'group';

// ONE set of milestones, drawn in ONE place at a time (owner 2026-10-12):
// COLLAPSED they all sit on the gag's own bar (shoot = the diamond, the other
// three = `BAR_MILESTONE_DEFS` markers); EXPANDED they all move to the
// Milestones lane, shoot included, and the gag's row draws none. Never both,
// so two markers for one date cannot disagree.
const BAR_MILESTONE_DEFS = MILESTONE_DEFS.filter((def) => def.field !== 'shoot_date');
// Which markers a COLLAPSED bar shows is `gantt/GanttMilestoneLegend` (shoot only
// by default; the legend switches the rest).
const milestoneOf = (gag, def) => ({
  ...def, key: `${gag.id}:${def.field}`, gag, date: dateOnly(gag[def.field]), liveKey: ganttGagDateLiveKey(gag.id, def.field),
});

// A task the chart can draw: one with at least one date (the panel Gantt's
// own rule). Undated tasks are COUNTED, never drawn and never drag-dated.
const isDatedTask = (t) => !!(t.start_date || t.due_date);
const entryTasks = (entry) => (entry && entry.detail && Array.isArray(entry.detail.tasks) ? entry.detail.tasks : null);

/** How many of an expanded gag's tasks have no dates (so have no row). */
export function undatedTaskCount(entry) {
  const tasks = entryTasks(entry);
  return tasks ? tasks.filter((t) => !isDatedTask(t)).length : 0;
}

/**
 * The child rows of ONE expanded gag. `gag` is the timeline row (its three
 * lane dates are already on it, so the lane needs no request); `entry` is
 * this gag's slot in the detail cache — `undefined` / `{ status: 'loading' }`
 * until `GET /api/gags/:id` lands, then `{ status: 'ready', detail }`, or
 * `{ status: 'error' }` (keeping the last `detail` when a REFRESH failed).
 * Tasks are in start-date order, the panel Gantt's default.
 */
/**
 * Split a lane's milestones over TWO rows when their labels would overlap
 * (owner 2026-10-12). In date order, a marker closer than `clearDays` to the
 * previous one on the first row drops to the second. Decided from the STORED
 * dates, so a marker does not hop rows mid-drag.
 */
export function splitLaneMilestones(milestones, clearDays) {
  if (!(clearDays > 0)) return [milestones, []];
  const first = [];
  const second = [];
  let lastFirst = null;
  for (const m of [...milestones].sort((a, b) => String(a.date).localeCompare(String(b.date)))) {
    const d = parseDate(m.date);
    if (lastFirst && d && daysDiff(lastFirst, d) < clearDays) { second.push(m); continue; }
    first.push(m);
    if (d) lastFirst = d;
  }
  return [first, second];
}

// A lane marker + its label is about this wide (12px shape, gap, ≤ 6 letters
// at 10px semibold); with no label (quarter zoom) just the shape and a gap.
const LANE_LABEL_CLEAR_PX = 56;
const LANE_SHAPE_CLEAR_PX = 16;

export function gagChildRows(gag, entry, laneClearDays = 0) {
  const rows = [];
  const milestones = MILESTONE_DEFS.filter((def) => gag[def.field]).map((def) => milestoneOf(gag, def));
  if (milestones.length > 0) {
    const [first, second] = splitLaneMilestones(milestones, laneClearDays);
    rows.push({ type: 'lane', key: `c-${gag.id}-lane`, gag, milestones: first });
    if (second.length > 0) rows.push({ type: 'lane', key: `c-${gag.id}-lane2`, gag, milestones: second, second: true });
  }

  const tasks = entryTasks(entry);
  if (!tasks) {
    const kind = entry && entry.status === 'error' ? 'error' : 'loading';
    rows.push({ type: 'note', key: `c-${gag.id}-note`, gag, kind });
    return rows;
  }
  const dated = tasks.filter(isDatedTask)
    .sort((a, b) => (a.start_date || '9999').localeCompare(b.start_date || '9999'));
  for (const task of dated) rows.push({ type: 'task', key: `c-${gag.id}-t-${task.id}`, gag, task });
  if (rows.length === 0) rows.push({ type: 'note', key: `c-${gag.id}-note`, gag, kind: 'empty' });
  return rows;
}
// The date axis's height is the SHARED Gantt constant (`gantt/GanttDateAxis`);
// re-exported under this name because ~15 scroll-arithmetic sites and the
// tests read it here.
export const HEADER_HEIGHT = GANTT_HEADER_HEIGHT;
const LABEL_WIDTH = 220;
const DIAMOND = 14; // px, marker square rotated 45° visual size

/**
 * How far the pointer may travel between press and release and still count as
 * a CLICK rather than a pan.
 *
 * 🚨 IT EXISTS BECAUSE `data-pan-skip` CAME OFF THE BARS (2026-09-22, owner:
 * *"Option 2 sounds good for now"*). The bars carried the mark
 * UNCONDITIONALLY while being, by their own comment, *"derived, never
 * draggable"* — so they protected no drag of their own and simply made the
 * chart dead to panning wherever a bar was, which is most of it. The mark now
 * follows `teams/CrossTeamGanttView.js`'s rule: it is carried only by an
 * element that owns a drag.
 *
 * 🪤 THE HAZARD THAT REPLACES IT: a mousedown and a mouseup on the same
 * element fire a `click` however far the pointer travelled in between, so
 * without this threshold every pan begun on a bar would also OPEN that gag on
 * release — a worse defect than the one being fixed. `dragEndTime` next door
 * solves the same problem for the shoot-diamond drag and cannot be reused:
 * it is stamped only when a diamond drag actually MOVED a date, and a pan
 * moves no date at all.
 */
const PAN_CLICK_SUPPRESS_PX = 5;

// ── ROW-SPACE → CHART SCROLL-SPACE — the ONE conversion, and the question
//    it is NOT ────────────────────────────────────────────────────────────
//
// `focusWindow` and every other row measurement in this file works in
// ROW-SPACE: the top of the first entry in `flatRows` is 0.
//
// The CHART pane (`[data-testid="gag-timeline-scroll"]`) has the date-axis
// block INSIDE the scrolled element — it is the first child of that pane's
// `width: totalWidth` wrapper, `height: HEADER_HEIGHT`. So a row at
// row-space `t` sits at `t + HEADER_HEIGHT` in that pane's CONTENT, and
// `rowTopToScrollTop` answers exactly one question: **what scrollTop puts
// row `t` at the top of the CHART pane.** Three consumers — the focus
// effect, the clamp-overflow centring and the mount scroll.
// `focusedMaxHeight` (`HEADER_HEIGHT + rowsHeight`) is the same arithmetic
// seen from the container side, and corroborates it.
//
// 🚨 THE LABEL SYNC IS A DIFFERENT QUESTION AND MUST NOT USE THIS.
// It is a bare `labels.scrollTop = chart.scrollTop`. On 2026-09-22 a
// subtraction of HEADER_HEIGHT was added to it — reasoned out of a code
// read, never looked at — and it BROKE a working screen; the owner caught
// it in a browser. **The two 48s cancel.** In SCREEN terms, which is the
// only frame that settles it, with `S` the chart's scrollTop and `L` the
// label pane's:
//
//   • The label column is `flex flex-col`. Its HEADER_HEIGHT info block is
//     `flex-shrink-0` and sits OUTSIDE `#gag-timeline-labels`, so the label
//     pane's BOX starts HEADER_HEIGHT below the chart body's top. Row `t`
//     is at content `t` there ⇒ on screen at `top + 48 + t − L`.
//   • The chart pane's box starts AT the chart body's top, and the same row
//     is at content `48 + t` ⇒ on screen at `top + 48 + t − S`.
//   • Equate them: **L = S.** One 48 is a BOX offset and the other a
//     CONTENT offset; they point opposite ways and cancel exactly.
//
// 🔑 Independent corroboration, if the algebra does not convince: the two
// panes' maximum scroll ranges are EQUAL. Labels `R − (B − 48)`, chart
// `(48 + R) − B`, both `R − B + 48` (R = rows height, B = body height).
// Only a 1:1 map preserves a range, so `L = S − 48` could never reach the
// label list's last 48px at all — 48px of names permanently unreachable.
//
// 🥇 THE LESSON, AT THE SITE, BECAUSE IT COST A LIVE SCREEN: A DEFECT CLAIM
// NEEDS THE SAME PROOF AS A FIX. The subtraction was "verified
// structurally" and every premise it cited was TRUE — the axis really is
// inside the chart scroller, the heading really is outside the label
// scroller — and the conclusion was still wrong, because a structural
// argument can be confidently wrong by assuming two coordinate frames share
// an origin they do not. It then propagated through a duplication scanner,
// a manifest reason, this docblock, a rewritten test and two briefs, each
// treating the last as settled. None of them was independent.
// 🚫 `scrollTopToRowTop` (the inverse) EXISTED ONLY TO SERVE THAT
// SUBTRACTION and is DELETED — pre-deletion SHA `0f189203`. Do not
// reintroduce it for the label sync: there is nothing to convert.
// 🪤 Clamps at zero: a negative scrollTop is not a thing, and a caller that
// has subtracted a viewport fraction (the mount scroll) can produce one.
export function rowTopToScrollTop(rowTop) {
  return Math.max(0, rowTop) + HEADER_HEIGHT;
}

// Shoot diamond — same rose family as the detail Gantt's milestone lane.
const DIAMOND_CLASS = 'text-rose-500 dark:text-rose-400';

// ── B2: the "No shoot date" placeholder ─────────────────────────────────
//
// ⚖️ THE PILL IS TEXT, AND THAT IS A RULING RATHER THAN A PREFERENCE. Root
// CLAUDE.md's sole-carrier rule: when a mark is the ONLY carrier of a state
// it needs a non-colour channel and 3:1 contrast, and a native `title` is
// hover-only so it reaches nobody on touch and nobody who never hovers.
// Text explains itself and dodges both. A red dot MAY sit alongside the pill
// — then it is redundant, which is the lower standard it can meet — but
// never INSTEAD of it. No dot is drawn here: the owner did not ask for one
// and the text already carries the state.
//
// 🪞 The shape is `budget/CrewGanttView.js`'s "No dates set" placeholder,
// which solved this exact problem: one wording string feeding BOTH the
// native `title` and an `sr-only` span (the `InactiveBadge.js` device) so
// the tooltip and the accessible name cannot drift apart. Its money trap
// does not apply — a shoot date is one date, not a span.
//
// 🔴 NO DRAGGABLE DIAMOND ON THE PLACEHOLDER, deliberately. It was raised
// and the owner did not ask for it, and what a drop would WRITE needs its
// own ruling. Out of scope; do not "finish the job" by adding one.
export const NO_SHOOT_DATE_LABEL = 'No shoot date';
export const NO_SHOOT_DATE_TITLE =
  'This gag has no shoot date yet. It is listed at the top of the timeline until one is set.';
const NO_SHOOT_PILL_HEIGHT = 20;

// ── B3: the LABEL-COLUMN HEADER — an info block, not a bare "Gag" label ──
//
// Owner, verbatim: `lets change "Gag ⚠ 1" into more of an info space, like
// same size but two or 3 lines "20 gags", "6 without shoot dates", "4
// without task planning" etc etc`.
//
// 🔻 ITEM 3 (2026-09-22, browser round): the block ORIGINALLY carried a
// TOTAL line ("N gags shown") above the exceptions. Owner, having looked at
// it rendered: *"this is good … but the new text is quite small so it will
// need to be made bigger unfortunatly. Increase the text and the row will
// also need to be taller to accomodate"* — then, on seeing the total line
// disagreed with the app bar's own gag count (41 vs 38, a SEPARATE
// cross-endpoint defect, tracked elsewhere and NOT fixed here): *"Once we
// fix the true number of gags we can keep the number here … and remove '38
// gags shown' thus allowing us to increase the text height without
// increaseing the row height."* **So the total line is DELETED outright —
// not merely made smaller — and `HEADER_HEIGHT` STAYS 48.** The app bar
// (`Breakdown Project Aurora · 41 gags · Est. cost: …`) is now the ONE place
// the gag count is stated; this block never restates it, so the two can
// never disagree again.
// 🪤 **The accessible `title`/`sr-only` sentence also DROPS the total,
// deliberately, not merely for symmetry with the visible line**: the app
// bar's count strip is `hidden lg:flex`, so on a narrow viewport it is
// absent from the accessibility tree too, not just visually — but restating
// a count here that was JUST shown to be wrong would put a stale, disputed
// number back into the one channel a screen-reader user has, which is worse
// than silence. The gap this leaves for narrow-viewport/AT users on a
// mobile-first app is real and is flagged in the PART F delta, not papered
// over here.
//
// 🔑 LINES 1-2 are a PRIORITISED exception list — at most two shown, fixed
// order (1) no shoot date, (2) no tasks planned, (3) needs a risk
// assessment — each HIDDEN AT ZERO, UNCHANGED from the original design. A
// THIRD non-zero category is never silently dropped: it is folded into the
// block's `title` + `sr-only` summary (`gagHeaderSummaryTitle` below) even
// when it has no visible line of its own. **If ALL THREE are zero the block
// renders NOTHING** — no bare "Gag" label was restored, because nobody
// asked for one; flagged as a possible gap in PART F, not assumed away.
//
// 📏 Arithmetic for 2 lines inside 48px, spending the space the deleted
// total line freed: `text-sm leading-[18px]` (18px) × 2 lines + `gap-0.5`
// (2px) = 38px worst case, vertically centred (`justify-center`) inside the
// fixed 48px cell — 10px of slack (~21%), comparable to the original
// single-badge layout's proportions. `text-sm` is 14px, a substantial step
// up from the old `text-[9px]` exception lines — the entire point of the
// change. 🚫 jsdom has no layout: this arithmetic is UNPROVEN and is a
// BROWSER check — Pete looks at it again, which is the real gate.
// 🔻 SUPERSEDED 2026-10-13: the lines are `text-xs leading-4` now, the legend's
// size (owner: "reduce this text ... to the same size as the 'not started' 'in
// progress' etc colour helpers"). 2 × 16px + 2px = 34px in the same 48px cell.
//
// 🔑 EACH COUNT IS OVER `filtered`, NEVER THE RAW PAYLOAD — same rule the
// original marker used.
//
// ⚖️ ITEM 5 (2026-09-22, same-day browser round 2): the exception lines
// went from amber-bold to MUTED GREY — owner, having looked at round-1's
// enlarged text: *"now '4 without shoot dates / 8 without task planning'
// is too bold and in my face!"*. Asked whether grey reads right, his own
// reason, verbatim: *"we could try grey if you think thats better? its
// not an urgent warning, just a For Your Infomation"*.
// 🔑 **THE RULE THIS SETTLES, FOR THE NEXT PERSON WHO SEES A COUNT OF GAGS
// MISSING SHOOT DATES AND WANTS TO MAKE IT "STAND OUT": this block is FYI,
// NOT AN ALERT.** It reports the state of the department; it does not
// assert that anything is wrong. A department with 8 gags not yet
// task-planned in pre-production is entirely normal — the count is only
// useful as a FACT, and dressing a normal fact as a warning is what makes
// chrome stop being read (the same failure this file's own B3 opening
// already names for a marker stuck at "0"). **If he later wants more
// presence, the dial-up is WEIGHT or the NUMBER alone — never a warning
// colour, because a colour would assert something the data does not
// support.** `font-medium`/`font-semibold` on the digits alone is the one
// line to change; do not reach for amber/red again without a fresh ruling.
//
// 🎨 `text-gray-500 dark:text-gray-400`, no `font-bold` — the EXACT pair
// (and weight-default) the pre-item-2 "Gag" column label already used,
// not a new choice. `amber-700`/`dark:amber-400` (INHERITED from
// `InactiveBadge.js`, never re-measured against this cell) is GONE from
// this file along with the WCAG 1.4.11 non-text 3:1 argument it carried —
// that argument no longer applies, because colour no longer carries any
// meaning here; the WORDS do. What applies instead is 1.4.3 for TEXT
// (4.5:1), a HIGHER bar than 1.4.11's 3:1.
// 🧮 COMPUTED (WCAG relative-luminance formula on the declared hex, NOT
// Chromium-measured — say so rather than implying a browser check ran):
// light `#6b7280` on this cell's own `bg-gray-50` (`#f9fafb`) ≈ **4.63:1**
// — passes 4.5:1, but THINLY; dark `#9ca3af` on `bg-gray-900` (`#111827`)
// ≈ **6.81:1** — comfortable. 🚫 Owed: re-measure the light pair in a real
// browser before treating 4.63 as safe headroom rather than a bare pass.
// 🚨 CASCADE CHECKED: `grep -n 'html.dark' client/src/index.css` — `:267`
// is `html.dark .text-gray-500 { color: #9ca3af !important; }` and `:271`
// is `html.dark .text-gray-400 { color: #6b7280 !important; }`. Neither
// fights this pair the way an inert `dark:` class usually does: this
// element carries the BARE `text-gray-500` token (not `dark:text-gray-500`),
// so `:267`'s `!important` is exactly what WINS in dark mode over the
// non-important `dark:text-gray-400` utility — and by construction the two
// end up equal (`:267`'s forced value IS gray-400's own hex), so the
// override does not change what renders, only which rule is doing the
// rendering. This is the SAME mechanism the original "Gag" label already
// relied on; nothing new is being trusted here.
export function noShootDateCountTitle(count) {
  return count === 1
    ? '1 gag has no shoot date. It is listed at the top of the timeline.'
    : `${count} gags have no shoot date. They are listed at the top of the timeline.`;
}

export function noTasksPlannedCountTitle(count) {
  return count === 1
    ? '1 gag has no tasks planned.'
    : `${count} gags have no tasks planned.`;
}

export function raRequiredCountTitle(count) {
  return count === 1
    ? '1 gag needs a risk assessment.'
    : `${count} gags need a risk assessment.`;
}

// The block's single `title` + `sr-only` sentence — the one place a THIRD
// non-zero category (folded out of the two visible lines) is still
// reachable, per the rule above. ⚠️ Carries NO total/"shown" count as of
// item 3 (2026-09-22) — see the docblock above for why. Each part is
// already a complete sentence (never joined with "and"), so dropping one
// never leaves a dangling conjunction. Returns `undefined`, never `''`, when
// every count is zero — the `dueIndicatorTitle` convention
// (`components/CLAUDE.md`) — so a caller spreading it into `title=` gets NO
// attribute rather than an empty one.
export function noOwnerCountTitle(count) {
  return count === 1 ? '1 gag has no owner.' : `${count} gags have no owner.`;
}

export function gagHeaderSummaryTitle({ noOwner, noShootDate, noTasksPlanned, raRequired }) {
  const parts = [];
  if (noOwner > 0) parts.push(noOwnerCountTitle(noOwner));
  if (noShootDate > 0) parts.push(noShootDateCountTitle(noShootDate));
  if (noTasksPlanned > 0) parts.push(noTasksPlannedCountTitle(noTasksPlanned));
  if (raRequired > 0) parts.push(raRequiredCountTitle(raRequired));
  return parts.length > 0 ? parts.join(' ') : undefined;
}

// ── Focus window (owner: "when I click a gantt chart item... I want to see
// some of the chart", "I want 3 gags to show") ──────────────────────────
//
// Pure function: given the same `rows` shape `flatRows` builds (a mixed list
// of `{ type: 'group', key, label, count }` and `{ type: 'gag', gag }`) and
// the currently selected gag id, returns the scroll offset + height of a
// WINDOW of EXACTLY 3 gag rows (fewer if the list has fewer than 3), the
// selected gag centred where possible and clamped at either end.
//
// 🔴 DELIBERATELY NO GROUP-HEADER LOGIC OF ANY KIND — no walk-back, no
// adjacency test. A prior version pulled in the group header immediately
// above the window's first gag when doing so was free; that was a genuine
// CRITICAL defect (reported by review): the walk-back this replaced did not
// stop at the window's own gags, so a mid-group selection swept in every
// earlier sibling back to that group's header, and once fixed to an
// adjacency test the header was included only when the window's first gag
// happened to be its group's first gag — inconsistent, and still fighting
// the same DOM constraint a header-in-the-slice approach can never escape:
// the window is a CONTIGUOUS scroll range, so "always show the header
// above" and "always show exactly 3 gags" cannot both hold once the header
// is not already adjacent. The owner then approved a STICKY group header
// (spreadsheet freeze-pane behaviour) instead, which supersedes this
// entirely — group context is now a permanent overlay, never spent out of
// the 3-gag budget. See the sticky-header rendering below and
// `clampFocusedMaxHeight`'s docblock for the GROUP_HEIGHT reservation this
// forces on the container's `maxHeight`.
//
// 🪤 A group header that happens to sit BETWEEN two of the window's OWN
// gags (because they belong to different groups) is NOT filtered out and
// still counts toward `height` — that is unavoidable, not a leftover of the
// deleted clause: the window is a literal pixel range in a rendered list,
// so a row physically between two included gags is part of what scrolls
// into view whether this function "wants" it there or not.
//
// 🪤 Row offsets are NOT `index * ROW_HEIGHT` — group headers use
// GROUP_HEIGHT, so both `scrollTop` and `height` are derived by walking the
// rows and summing the SAME per-row height `totalRowHeight` already uses
// (`rowHeight(r)` — group, gag and child rows each differ), never by multiplying
// an index. A flat (ungrouped) list happens to make a naive multiply agree,
// which is exactly what makes the mistake ship unnoticed.
export function focusWindow(rows, selectedGagId) {
  if (selectedGagId == null) return null;

  // Positions (indices into `rows`) of the gag rows, in order.
  const gagPositions = [];
  rows.forEach((r, i) => { if (isGagRow(r)) gagPositions.push(i); });

  const foundOrderIdx = gagPositions.findIndex(
    (idx) => rows[idx].gag && rows[idx].gag.id === selectedGagId
  );
  if (foundOrderIdx === -1) return null;

  const total = gagPositions.length;
  let start = Math.max(0, foundOrderIdx - 1);
  let end = start + 2;
  if (end > total - 1) {
    end = total - 1;
    start = Math.max(0, end - 2);
  }

  const sliceStartIdx = gagPositions[start];
  // 🔀 EXPANDED ROWS AND THE WINDOW (2026-10-12). The window is still "3 gag
  // rows", a contiguous range from the first of them to the last:
  //   • child rows BETWEEN two window gags are inside it by position (so the
  //     selected gag's own tasks show, unless it is the window's last gag);
  //   • when the SELECTED gag is the window's last gag, its child rows are
  //     added — cutting the window at its row would leave the tasks of the gag
  //     whose panel you just opened below the fold;
  //   • a NEIGHBOUR that is the window's last gag does NOT bring its child rows
  //     in. The window exists to keep the panel below in view (the owner's
  //     first complaint), and a neighbour's task list is not what was selected.
  let sliceEndIdx = gagPositions[end];
  if (end === foundOrderIdx) {
    while (sliceEndIdx + 1 < rows.length && isChildRow(rows[sliceEndIdx + 1])) sliceEndIdx += 1;
  }

  let scrollTop = 0;
  for (let i = 0; i < sliceStartIdx; i += 1) scrollTop += rowHeight(rows[i]);
  let height = 0;
  for (let i = sliceStartIdx; i <= sliceEndIdx; i += 1) height += rowHeight(rows[i]);

  return { scrollTop, height };
}

// The chart body's maxHeight while focused: HEADER_HEIGHT (the date-axis
// bar) plus a rows-height figure the CALLER builds, capped at the SAME
// 600px ceiling the chart carries when nothing is selected — focus only
// ever SHRINKS the chart, never grows it. The caller passes
// `GROUP_HEIGHT + win.height`, never `win.height` alone — the sticky group
// header (render site below) reserves its own row's worth of space
// unconditionally, on top of whatever `focusWindow` already summed.
//
// 🔻 THIS DOCBLOCK CLAIMED THE CLAMP WAS UNREACHABLE UNTIL 2026-09-22 AND
// THE CLAIM WAS FALSE. It read: "A real `focusWindow` window is structurally
// bounded well under 600px (at most 3 gag rows plus at most 3 interposed
// group headers …), so no `flatRows` shape the app can ever build reaches
// this clamp today."
//
// 🚨 The reasoning bounded the group headers INTERPOSED BETWEEN the window's
// three gags at 2, which is right, and then ignored COLLAPSED groups
// entirely. Collapsing suppresses a group's gag rows but KEEPS its header in
// `flatRows`, and nothing bounds how many collapsed groups sit in that same
// gap. Review built the counter-example: one open group (1 gag), 50
// COLLAPSED groups, one open group (2 gags) → `win.height` 1962, i.e. the
// clamp not merely reached but exceeded threefold. See `focusRowScrollTop`
// for what that did to the selected row, and §A1 in the test file, which
// builds the shape rather than quoting the numbers.
//
// 🕶️ The lesson is this repo's own: an UNREACHABILITY claim is worth nothing
// until someone tries it. This one was argued, written into a docblock AND a
// test-file header, and shipped — and a feature (the centring fallback) was
// left unbuilt because everyone believed it.
//
// Testing the clamp FORMULA directly, with a raw height number rather than
// through `focusWindow`'s real output, remains right and is unchanged: it
// keeps the clamp from being deleted as "dead code" by someone who only
// checked today's inputs.
export function clampFocusedMaxHeight(rowsHeight) {
  return Math.min(600, HEADER_HEIGHT + rowsHeight);
}

// ── A3: the sticky header's reservation is CONDITIONAL on there being one ──
//
// THE ONE PREDICATE for "does this render contain any group rows", consumed
// by the focus effect's reservation AND answering the same question the row
// map asks per row. `flatRows` is the array the JSX maps over and the
// `row.type === 'group'` branch is the only thing that renders a sticky
// header, so "some row is a group" IS "a sticky header renders" — one
// derivation, no convention to keep in step.
//
// 🚨 THE DEFECT THIS CLOSES: the reservation was UNCONDITIONAL. In flat mode
// (`groupMode === 'all'`) `flatRows` contains no group rows at all, so no
// sticky header is rendered — and the focused chart still reserved
// GROUP_HEIGHT for it, leaving 36px of dead space at the bottom of the
// window on every flat-mode selection.
//
// 🪤 It is deliberately NOT `groupMode !== 'all'`. That is a second way of
// asking the same question and it is wrong in a reachable case: grouped mode
// over an EMPTY filtered set also produces zero group rows. Ask the rows,
// never the mode.
export function stickyHeaderReserve(rows) {
  return rows.some((r) => r.type === 'group') ? GROUP_HEIGHT : 0;
}

// ── A1: where to scroll, in ROW-SPACE, for a focused selection ───────────
//
// Row-space top of the selected gag's OWN row, or null if it is not present.
// Walks and sums the same per-row heights everything else here does.
export function selectedRowTop(rows, selectedGagId) {
  if (selectedGagId == null) return null;
  let offset = 0;
  for (const r of rows) {
    if (isGagRow(r) && r.gag && r.gag.id === selectedGagId) return offset;
    offset += rowHeight(r);
  }
  return null;
}

// THE focused scroll target, in ROW-SPACE. Two regimes:
//
//   • the window FITS the clamped viewport → scroll to the window's top,
//     which is what this feature has always done and what keeps "3 gags
//     show" literally true;
//   • the window does NOT fit → CENTRE the selected gag in the part of the
//     viewport the sticky header does not cover.
//
// 🚨 WHY THE SECOND REGIME EXISTS (review finding A1, and it was a reachable
// CRITICAL, not a theoretical one). Collapsing a group SUPPRESSES its gag
// rows but KEEPS its header in `flatRows`, and the number of collapsed
// groups is unbounded. Reviewer's shape — one open group (1 gag), 50
// COLLAPSED groups, one open group (2 gags) — gives `win.height` 1962 with
// the second gag sitting 1878px past the window's top. `clampFocusedMaxHeight`
// correctly caps the viewport at 600px, so scrolling to the window's top put
// the gag the user had just clicked 1878px below a 600px viewport: entirely
// off screen. That is worse than the bug the focus feature fixed. (Those two
// figures are reproduced exactly by §A1 in the test file, which builds that
// shape rather than trusting them.)
//
// 🪤 The result is CLAMPED to the real scroll range, not just floored at 0.
// A browser clamps `scrollTop` to `scrollHeight - clientHeight` anyway, so an
// unclamped value would land somewhere the caller did not compute and the
// arithmetic would stop being testable. Selecting one of the last gags
// therefore centres as far as it can and then stops — the same honest
// behaviour the window regime already has at either end of the list.
// ── B4: on mount, scroll to the CLOSEST shoot date ──────────────────────
//
// Owner: "it should scroll down to the closest shoot date if one exists.
// otherwise stay at the top."
//
// 🔑 CLOSEST means the smallest ABSOLUTE distance from today, in either
// direction — not the next one coming up. That lands the view on where the
// production actually is: mid-shoot, the nearest date is often yesterday's,
// and a "next upcoming" rule would scroll past the work in progress.
//
// Returns the ROW-SPACE top of that gag's row, or null when no gag has a
// shoot date at all (the "otherwise stay at the top" arm — the caller does
// nothing rather than scrolling to 0, so it cannot fight anything else).
//
// 🪤 Ties break to the EARLIER row, because the scan keeps the first strict
// minimum. With `rows` in render order that is the topmost of the tied gags,
// which is the stable, explicable answer; `<=` would silently prefer the
// last and make the landing depend on sort order.
export function closestShootRowTop(rows, todayStr) {
  const today = parseDate(todayStr);
  if (!today) return null;
  let bestTop = null;
  let bestDist = Infinity;
  let offset = 0;
  for (const r of rows) {
    if (isGagRow(r) && r.gag && r.gag.shoot_date) {
      const d = parseDate(r.gag.shoot_date);
      if (d) {
        const dist = Math.abs(daysDiff(today, d));
        if (dist < bestDist) { bestDist = dist; bestTop = offset; }
      }
    }
    offset += rowHeight(r);
  }
  return bestTop;
}

// ── A4: WHICH PANE IS THE VIEWPORT, AND THE 48px THAT IS NOT `rowTopToScrollTop`'s
//
// 🚨 THE FOCUS WINDOW IS READ IN THE **LABEL PANE**, AND THE LABEL PANE IS
// `HEADER_HEIGHT` SHORTER THAN THE CHART PANE. The chart body's `maxHeight`
// covers both columns; the label column spends its first HEADER_HEIGHT on
// the info block OUTSIDE `#gag-timeline-labels`, so the labels viewport is
// `maxHeight − HEADER_HEIGHT` and a row at row-space `t` is at its top when
// the chart's `scrollTop` is `t` — NOT `rowTopToScrollTop(t)`. The two
// HEADER_HEIGHTs cancel here for exactly the reason they cancel in the label
// sync, and `rowTopToScrollTop`'s docblock derives it.
//
// 🔻 THE REGRESSION THIS CLOSES, reported by the owner the evening it
// shipped: *"the selected gag sits at the TOP of the window instead of the
// MIDDLE"*. The effect wrote `rowTopToScrollTop(rowScrollTop)`, 48px too far
// down, so the label list started 48px INTO the three-gag window. MEASURED in
// Chromium on a replica of this box model, selecting gag 20 of a flat 41:
// scrollTop 846 showed labels 20 (clipped, 6px above the pane top), 21, 22;
// scrollTop 798 shows 19, 20, 21. In GROUPED mode it was worse than reported
// — the selected row's only visible 36px landed exactly under the sticky
// group header, so its label was not on screen at all.
//
// 🥇 THIS IS THE THIRD 48px FRAME CONFUSION ON THIS COMPONENT IN ONE DAY
// (a label sync that subtracted 48 where the two cancel; a speculated fourth
// on the Teams milestone lane, falsified; this one). Three bugs from one
// ambiguity is not three careless edits — it is the code failing to say which
// frame a number is in. **A scroll offset should not cross a function
// boundary without its frame in its NAME**, because a comment is read by
// whoever already suspects there is a question. `rowTopToScrollTop` is named
// for its frame and is correct; this function's return value is ROW-SPACE
// measured from the LABEL pane's top, which is what `focusRowScrollTop`'s
// caller assigns raw — see the assignment site, which says so at the line.
//
// 🪤 The reserve is space ON TOP OF the window, which is what reserving it
// is for: the window's first row starts `reserve` into the pane, never at 0
// where the sticky group header would sit on top of it. Floored at 0, so the
// first gag in a grouped list does not ask for a negative scroll.
export function focusRowScrollTop(rows, selectedGagId, win) {
  if (!win) return 0;
  const reserve = stickyHeaderReserve(rows);
  const maxHeight = clampFocusedMaxHeight(reserve + win.height);
  // The LABEL pane's height — the viewport the window is actually read in.
  const viewport = maxHeight - HEADER_HEIGHT;
  // The whole window is visible below the sticky overlay: show it from the
  // overlay's lower edge down. (Same predicate as before, one HEADER_HEIGHT
  // moved to the other side; it is the container-side test seen from the
  // label pane.)
  if (reserve + win.height <= viewport) return Math.max(0, win.scrollTop - reserve);

  const selTop = selectedRowTop(rows, selectedGagId);
  if (selTop == null) return Math.max(0, win.scrollTop - reserve);

  // Viewport shows row-space [s, s + viewport); the sticky header covers its
  // first `reserve` px, so the UNCOVERED band is [s + reserve, s + viewport).
  // Put the selected row's centre at that band's centre and solve for s.
  const uncovered = viewport - reserve;
  const ideal = selTop + ROW_HEIGHT / 2 - reserve - uncovered / 2;

  const totalRowHeight = rows.reduce((sum, r) => sum + rowHeight(r), 0);
  const maxScroll = Math.max(0, totalRowHeight - viewport);
  return Math.max(0, Math.min(Math.round(ideal), maxScroll));
}

// Which open gag a task belongs to — read from the cache the rows were built
// from, never from a field on the task.
function gagIdOfTask(details, taskId) {
  for (const id of Object.keys(details)) {
    const tasks = entryTasks(details[id]);
    if (tasks && tasks.some((t) => t.id === taskId)) return Number(id);
  }
  return null;
}

// A task bar's colour, as in the gag panel's Gantt (`statusColorFallback`):
// its TEAM's colour, else its STATUS colour — so one task looks the same in
// both charts.
const taskBarColor = (t) => t.team_color || TASK_STATUS_COLORS_HEX[t.status] || '#6B7280';

// The quiet line an expanded gag shows when it has no task rows to draw.
const CHILD_NOTE_TEXT = {
  loading: 'Loading tasks…',
  error: 'Could not load tasks.',
  empty: 'No dated tasks or milestones',
};

export function undatedTasksTitle(count) {
  return count === 1
    ? '1 task has no dates, so it is not on the timeline. Open the gag to give it dates.'
    : `${count} tasks have no dates, so they are not on the timeline. Open the gag to give them dates.`;
}

/**
 * The drag zones of a task bar the viewer MAY move. WHICH zones a bar offers
 * is the shared rule (`ganttTaskDragZones`: a one-day bar is half "move", half
 * "extend"; a longer bar has two edge handles from 24px wide); the cursors are
 * the shared ones (`constants/ganttCursors.js`). `onStart(e, handle)`.
 */
function TaskDragZones({ task, width, onStart }) {
  const { isSingleDay, showBothHandles } = ganttTaskDragZones(task, width);
  const press = (handle) => ({
    onMouseDown: (e) => onStart(e, handle),
    onTouchStart: (e) => onStart(e, handle),
  });
  const grip = <div className="w-0.5 h-2.5 rounded-sm bg-white/60" />;
  return (
    <>
      {showBothHandles && (
        <div
          data-testid={`gag-timeline-task-move-${task.id}`}
          className={`absolute top-0 h-full ${GANTT_CURSOR_CLASS.move} z-0`}
          style={{ left: 12, right: 12 }}
          {...press('body')}
        />
      )}
      {showBothHandles && (
        <div
          data-testid={`gag-timeline-task-left-${task.id}`}
          className={`absolute top-0 left-0 w-3 h-full ${GANTT_CURSOR_CLASS.resize} flex items-center justify-center z-20`}
          {...press('left')}
        >
          {grip}
        </div>
      )}
      <div
        data-testid={`gag-timeline-task-right-${task.id}`}
        className={`absolute top-0 right-0 h-full ${GANTT_CURSOR_CLASS.resize} flex items-center justify-center z-20 ${isSingleDay ? 'w-1/2' : 'w-3'}`}
        {...press('right')}
      >
        {grip}
      </div>
      {isSingleDay && (
        <div
          data-testid={`gag-timeline-task-move-${task.id}`}
          className={`absolute top-0 left-0 w-1/2 h-full ${GANTT_CURSOR_CLASS.move} z-10`}
          {...press('body')}
        />
      )}
    </>
  );
}

/**
 * The confirm step every chart move goes through (a date, a task) — the gag
 * panel's Gantt asks the same question for the same kind of move.
 * 🧱 PORTALLED at the z-60 escape tier: the chart root is `isolate`, so an
 * in-tree `fixed` backdrop would sit UNDER the app bar and the Toolbar.
 */
function MoveConfirmDialog({ title, children, busy, onCancel, onConfirm }) {
  return createPortal((
    <div data-testid="gag-timeline-confirm" className="fixed inset-0 flex items-center justify-center bg-black/50" style={{ zIndex: 60 }} onClick={onCancel}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-3">{title}</h3>
        {children}
        <div className="flex justify-end gap-2">
          <button type="button" data-testid="gag-timeline-confirm-cancel" onClick={onCancel} className={BTN_XS.secondary}>Cancel</button>
          <button type="button" data-testid="gag-timeline-confirm-ok" onClick={onConfirm} disabled={busy} className={BTN_XS.confirm}>
            {busy ? 'Saving...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}

// One "old date → new date" line of that dialog.
function DateChange({ label, from, to }) {
  return (
    <div>
      {label ? `${label}: ` : null}
      <span className="line-through text-gray-400 dark:text-gray-500">{formatDate(from)}</span>
      {' '}&rarr;{' '}
      <span className="text-blue-600 dark:text-blue-400 font-semibold">{formatDate(to)}</span>
    </div>
  );
}

function ShootDiamond({ className = '' }) {
  return (
    <svg
      className={`flex-shrink-0 ${className}`}
      width={DIAMOND} height={DIAMOND} viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"
    >
      <polygon points="6,0.5 11.5,6 6,11.5 0.5,6" />
    </svg>
  );
}

export default function GagTimelineView({
  filters,
  userId,
  activeDeptId,
  refreshToken,
  selectedGagId,
  expandRequest,
  onOpenGag,
  onChanged,
  // 🔀 STATUS TABS (2026-10-02): `filters.statuses` carries the page's active
  // status tab here (`[]` for All). This callback receives the per-tab counts
  // over the rows the search leaves — exactly what each tab would show —
  // because this view filters its own payload (title-only search, dateless gags
  // included), so counts taken from the Table's list could disagree with it.
  onStatusCounts,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToastOrNoop();
  // ── Expanded gags (2026-10-12). SESSION state seeded from a literal — do
  // not read or write a stored value (the page persists no view state).
  // `details` is the per-gag cache of `GET /api/gags/:id`:
  // `{ [gagId]: { status: 'loading' | 'ready' | 'error', detail } }`.
  const [expanded, setExpanded] = useState({});
  const [details, setDetails] = useState({});
  const detailSeq = useRef({});
  // Seeded from a LITERAL, deliberately — see DEFAULT_GROUP_MODE's docblock.
  // Do not reintroduce a load-time read of any stored value here.
  const [groupMode, setGroupMode] = useState(DEFAULT_GROUP_MODE);
  // Milestone kinds switched off from the legend. It governs a COLLAPSED gag's
  // markers; an EXPANDED gag's Milestones lane shows all four.
  const { isHidden: isMsHidden, toggle: toggleMs } = useMilestoneVisibility();
  const [collapsed, setCollapsed] = useState({});
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [tooltip, setTooltip] = useState(null); // { gag, x, y }

  const timelineRef = useRef(null);
  // Zoom state, pixels per day and the keep-the-centre rule (shared).
  const { zoom, dayWidth, zoomTo, applyCentre } = useGanttZoom(timelineRef);
  // The chart body — the element whose `max-height` animates. Its
  // `transitionend` is the only honest signal that the focus shrink has
  // actually landed; see the A5 effect below for why nothing earlier will do.
  // 🪤 HELD IN STATE VIA THE SETTER AS A CALLBACK REF, NOT IN A `useRef`, AND
  // THAT IS THIS FILE'S OWN LESSON ARRIVING A SECOND TIME. This component
  // renders its chart behind three early returns (loading, error,
  // empty-filter), so at mount the element does not exist; `ref.current` is
  // not a reactive value, so an effect keyed on a ref never sees it arrive
  // and the listener is never attached — exactly how `useDragScroll` left
  // this timeline's pan DEAD in production (see `client/src/hooks/CLAUDE.md`).
  // A state setter passed as `ref` is called by React with the node on mount
  // and `null` on unmount, so the effect below re-runs at the right moments
  // with no dependency-array trap. I wrote the `useRef` version first and the
  // test for the re-assert caught it.
  const [chartBodyEl, setChartBodyEl] = useState(null);
  // 🔑 DRAG-PANNING IS THE SECOND WRITER OF THE LABELS' scrollTop and is easy
  // to miss: it writes the property directly, bypassing this pane's own
  // `onScroll`. The two paths must write the SAME number or they race — so
  // the hook mirrors 1:1 and so does the `onScroll` below. 🚫 Do not
  // reintroduce an offset option here; see `rowTopToScrollTop`'s docblock.
  useDragScroll(timelineRef, { axis: 'both', syncLabelsId: 'gag-timeline-labels' });
  const fetchSeq = useRef(0);
  const firstLoadDone = useRef(false);
  const dragEndTime = useRef(0);

  // Focus window (owner: keep ~3 gags visible around the selected row so the
  // panel opening below still leaves "some of the chart" in view). null
  // means "no focus active" — the container falls back to its 600px ceiling.
  const [focusedMaxHeight, setFocusedMaxHeight] = useState(null);

  // Markers nearer than this many days get a second Milestones row.
  const laneClearDays = Math.ceil((zoom === 'quarter' ? LANE_SHAPE_CLEAR_PX : LANE_LABEL_CLEAR_PX) / dayWidth);

  // ── Fetch — sequence-guarded like the page's /api/gags fetch ──
  const fetchRows = useCallback(async (silent = false) => {
    const seq = ++fetchSeq.current;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const qs = activeDeptId ? `?department_id=${activeDeptId}` : '';
      const res = await apiGet(`/api/gags/timeline${qs}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (seq !== fetchSeq.current) return; // stale response
      const list = Array.isArray(data.gags) ? data.gags : [];
      // Normalise dates to bare YYYY-MM-DD (Postgres may serialise with time).
      setRows(list.map((g) => ({
        ...g,
        shoot_date: dateOnly(g.shoot_date),
        location_access_date: dateOnly(g.location_access_date),
        prep_date: dateOnly(g.prep_date),
        strike_date: dateOnly(g.strike_date),
        window_start: dateOnly(g.window_start),
        window_end: dateOnly(g.window_end),
      })));
    } catch {
      if (seq !== fetchSeq.current) return;
      setError('Failed to load the timeline');
    }
    if (seq === fetchSeq.current) setLoading(false);
  }, [activeDeptId]);

  // Initial load shows the spinner; dept switches and refreshToken bumps
  // refetch silently (the chart stays mounted).
  useEffect(() => {
    fetchRows(firstLoadDone.current);
    firstLoadDone.current = true;
  }, [fetchRows, refreshToken]);

  // ── One gag's tasks: ONE request per gag opened (never one per task) ──
  // Sequence-guarded per gag, like the timeline fetch above. A REFRESH keeps
  // the rows it had while it is in flight and if it fails, so an open gag
  // never blanks; only a first load that fails shows the "could not load" note.
  // 🪤 Task dates are normalised to bare YYYY-MM-DD here, once: `parseDate`
  // answers null for a datetime string, and a null bound aborts a drag.
  const fetchDetail = useCallback(async (gagId) => {
    const seq = (detailSeq.current[gagId] || 0) + 1;
    detailSeq.current[gagId] = seq;
    const settle = (entryFrom) => {
      if (detailSeq.current[gagId] !== seq) return; // stale response
      setDetails((prev) => ({ ...prev, [gagId]: entryFrom(prev[gagId]) }));
    };
    setDetails((prev) => ({ ...prev, [gagId]: { status: 'loading', detail: prev[gagId]?.detail } }));
    try {
      const res = await apiGet(`/api/gags/${gagId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const tasks = (Array.isArray(data.tasks) ? data.tasks : []).map((t) => ({
        ...t, start_date: dateOnly(t.start_date), due_date: dateOnly(t.due_date),
      }));
      settle(() => ({ status: 'ready', detail: { ...data, tasks } }));
    } catch {
      settle((had) => ({ status: 'error', detail: had?.detail }));
    }
  }, []);

  const toggleExpand = (g) => {
    const opening = !expanded[g.id];
    setExpanded((prev) => ({ ...prev, [g.id]: opening }));
    // Loaded once, kept across a collapse: re-expanding asks nothing.
    if (opening && !details[g.id]?.detail && details[g.id]?.status !== 'loading') fetchDetail(g.id);
  };

  // `expandRequest` ({ id, n }): the PAGE asks for one gag's child rows to be
  // opened, as a click opens them. A browser Back that reopens a gag sends it
  // (2026-10-13), so the rows return with the panel. Deliberately NOT tied to
  // `selectedGagId`: "panel open, rows shut" is a real state (a `?gag=` link
  // opens the panel alone) and several cases pin it. `n` makes each request new.
  useEffect(() => {
    const id = expandRequest?.id;
    if (id == null || expanded[id]) return;
    setExpanded((prev) => ({ ...prev, [id]: true }));
    if (!details[id]?.detail && details[id]?.status !== 'loading') fetchDetail(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandRequest]);

  // Another department: its gags are other gags. Drop every expansion.
  const expandedDept = useRef(activeDeptId);
  useEffect(() => {
    if (expandedDept.current === activeDeptId) return;
    expandedDept.current = activeDeptId;
    detailSeq.current = {};
    setExpanded({});
    setDetails({});
  }, [activeDeptId]);

  // ── Two views of one gag: a save in the PANEL below (refreshToken) must
  // show in the open rows here. Every EXPANDED gag is re-read (one request
  // each); a collapsed gag's cached tasks are dropped instead, so it fetches
  // nothing now and is fresh the next time it is opened. 🪤 Not "only the
  // gag whose panel is open": a Save-and-close, or a save followed by opening
  // another gag, bumps the token in the same render the selection changes.
  const seenRefreshToken = useRef(refreshToken);
  useEffect(() => {
    if (seenRefreshToken.current === refreshToken) return;
    seenRefreshToken.current = refreshToken;
    const open = Object.keys(expanded).filter((id) => expanded[id]);
    setDetails((prev) => {
      const kept = {};
      for (const id of open) if (prev[id]) kept[id] = prev[id];
      return kept;
    });
    for (const id of open) fetchDetail(Number(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the token alone; `expanded` is read as it stands when it bumps
  }, [refreshToken, fetchDetail]);

  // ── Client-side filter subset (fields the timeline payload carries).
  // q matches TITLE only (the list search also covers description/location
  // server-side — those fields aren't in this payload).
  // The search alone — the set the status tab counts are taken over.
  const beforeStatus = useMemo(() => {
    const q = String(filters.q || '').trim().toLowerCase();
    return q ? rows.filter((g) => String(g.title || '').toLowerCase().includes(q)) : rows;
  }, [rows, filters.q]);

  const filtered = useMemo(
    () => (filters.statuses && filters.statuses.length
      ? beforeStatus.filter((g) => filters.statuses.includes(g.status))
      : beforeStatus),
    [beforeStatus, filters.statuses],
  );

  // Report the tab counts only once the payload has landed (`null` while loading
  // or failed), so the page shows no number rather than a confident row of zeros.
  const statusCounts = useMemo(
    () => (loading || error ? null : gagStatusTabCounts(beforeStatus)),
    [beforeStatus, loading, error],
  );
  useEffect(() => {
    if (onStatusCounts) onStatusCounts(statusCounts);
  }, [statusCounts, onStatusCounts]);

  // ── Group by sub-department (alphabetical, "No sub-department" last).
  // Row order within a group keeps the server sort (shoot, window, title).
  const groups = useMemo(() => {
    const map = new Map();
    for (const g of filtered) {
      const key = g.sub_department_id != null ? `sub:${g.sub_department_id}` : 'sub:none';
      if (!map.has(key)) map.set(key, { key, label: g.sub_department_name || 'No sub-department', gags: [] });
      map.get(key).gags.push(g);
    }
    // B2: dateless gags to the top of their own group too. `filtered` keeps
    // the server's order, which is shoot-date NULLs-LAST — the opposite —
    // so this is a real re-sort, not a no-op. `sort` is stable, so within
    // each block the server's ordering survives untouched.
    for (const grp of map.values()) {
      grp.gags.sort((a, b) => (hasNoShootDate(a) === hasNoShootDate(b) ? 0 : (hasNoShootDate(a) ? -1 : 1)));
    }
    const arr = [...map.values()];
    arr.sort((a, b) => ((a.key === 'sub:none') - (b.key === 'sub:none')) || a.label.localeCompare(b.label));
    return arr;
  }, [filtered]);

  const flatRows = useMemo(() => {
    // Flat mode: one continuous row list, NO group headers, shoot order
    // (server-mirrored comparator — see flatOrder).
    // An EXPANDED gag is followed directly by its child rows (the row model
    // at `rowHeight`); a collapsed gag contributes its own row only.
    const out = [];
    const pushGag = (g) => {
      out.push({ type: 'gag', gag: g });
      if (expanded[g.id]) out.push(...gagChildRows(g, details[g.id], laneClearDays));
    };
    if (groupMode === 'all') {
      [...filtered].sort(flatOrder).forEach(pushGag);
      return out;
    }
    for (const grp of groups) {
      out.push({ type: 'group', key: grp.key, label: grp.label, count: grp.gags.length });
      if (!collapsed[grp.key]) grp.gags.forEach(pushGag);
    }
    return out;
  }, [groups, collapsed, groupMode, filtered, expanded, details, laneClearDays]);

  // B3: counted over `filtered`, never `rows` — the info block must describe
  // what is ON SCREEN. Counting the raw payload would claim gags the active
  // filters have removed, which reads as a bug the moment anyone filters.
  const noShootDateCount = filtered.filter(hasNoShootDate).length;
  // Strict `=== 0` / `=== 'required'`, deliberately NOT `!g.task_count` —
  // a gag whose payload omits the field (never happens from the real API,
  // which always coerces it; can happen from an older fixture) must not be
  // silently counted as an exception it says nothing about.
  const noTasksPlannedCount = filtered.filter((g) => g.task_count === 0).length;
  const raRequiredCount = filtered.filter((g) => g.ra_status === 'required').length;
  // Fixed priority order (1) no shoot date (2) no tasks (3) needs an RA;
  // only the first two non-zero entries get a visible line, per the ruling
  // above `gagHeaderSummaryTitle`.
  // "N without an owner" leads since 2026-10-13 (owner): it is the one a person
  // has to act on, so a done or cancelled gag is not counted. Two visible lines at most.
  const noOwnerCount = filtered.filter(needsOwner).length;
  const headerExceptions = [
    noOwnerCount > 0 && {
      key: 'no-owner',
      testId: 'gag-timeline-no-owner-count',
      text: `${noOwnerCount} without an owner`,
    },
    noShootDateCount > 0 && {
      key: 'no-shoot-date',
      testId: 'gag-timeline-no-shoot-count',
      text: `${noShootDateCount} without shoot date${noShootDateCount === 1 ? '' : 's'}`,
    },
    noTasksPlannedCount > 0 && {
      key: 'no-tasks',
      testId: 'gag-timeline-no-tasks-count',
      text: `${noTasksPlannedCount} without task planning`,
    },
    raRequiredCount > 0 && {
      key: 'ra-required',
      testId: 'gag-timeline-ra-required-count',
      text: `${raRequiredCount} needing a risk assessment`,
    },
  ].filter(Boolean);
  const visibleHeaderExceptions = headerExceptions.slice(0, 2);
  // No `shown` count — item 3 dropped it from both the visible lines and
  // the accessible summary; see the B3 docblock above for why.
  const headerSummaryTitle = gagHeaderSummaryTitle({
    noOwner: noOwnerCount,
    noShootDate: noShootDateCount,
    noTasksPlanned: noTasksPlannedCount,
    raRequired: raRequiredCount,
  });

  const totalRowHeight = flatRows.reduce((sum, r) => sum + rowHeight(r), 0);
  const toggleGroup = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  // ── Focus on select: shrink to a 3-gag window around selectedGagId ──
  // Deselecting restores the 600px ceiling and DELIBERATELY leaves scrollTop
  // untouched — the row the user just closed is where their attention is.
  //
  // 🪤 `flatRows` gets a FRESH ARRAY REFERENCE on every recompute, including
  // a silent `fetchRows(true)` from a `refreshToken` bump (e.g. the user's
  // own detail-panel autosave while the panel is open) — that is an
  // IDENTITY change, not necessarily a CONTENT change. Re-applying scrollTop
  // on every identity change would throw away any scrolling the user did
  // inside the focused window the moment a background refetch lands. So the
  // scroll assignment is keyed on the computed WINDOW actually changing
  // (a real re-focus, or the window's shape genuinely moving — e.g. the
  // gag's position shifted after a re-sort), never on `flatRows`' identity
  // alone. `maxHeight` has no such hazard (setting it to its current value
  // is a no-op re-render) and is still kept in sync on every recompute.
  const lastAppliedWindowRef = useRef(null);
  useEffect(() => {
    if (selectedGagId == null) {
      setFocusedMaxHeight(null);
      lastAppliedWindowRef.current = null;
      return;
    }
    const win = focusWindow(flatRows, selectedGagId);
    if (!win) {
      setFocusedMaxHeight(null);
      lastAppliedWindowRef.current = null;
      return;
    }
    // The sticky group header (see its render site below) overlays the top
    // of the scroll viewport, so its own row's worth of space is reserved on
    // top of win.height — otherwise the user sees the header plus only 2 gag
    // rows, quietly breaking "I want 3 gags to show" via the very feature
    // meant to help.
    // 🔻 A3: the reservation is CONDITIONAL since 2026-09-22. It was
    // unconditional, and flat mode renders no group header at all, so every
    // flat-mode selection reserved 36px for something that was not there.
    // `stickyHeaderReserve` is the one predicate, derived from the same
    // `flatRows` the JSX maps over.
    const reserve = stickyHeaderReserve(flatRows);
    setFocusedMaxHeight(clampFocusedMaxHeight(reserve + win.height));
    // 🔑 A1: NOT `win.scrollTop`. When the window exceeds the clamp, that
    // would scroll to the top of a window taller than the viewport and leave
    // the selected gag off screen entirely; `focusRowScrollTop` falls back to
    // centring it. Still ROW-SPACE — the conversion happens on assignment.
    const rowScrollTop = focusRowScrollTop(flatRows, selectedGagId, win);
    const prev = lastAppliedWindowRef.current;
    // 🔀 EXPAND / COLLAPSE IS NOT A RE-FOCUS (2026-10-12). Opening a gag (or
    // its tasks arriving) makes the list taller and so moves the computed
    // window — but the user is looking at the gag they just clicked, whose
    // own row did not move. So for that one change the scroll is LEFT where
    // it is, and the new target is recorded as already applied (`target`),
    // so the next silent refetch does not read it as a change either.
    // `scrollTop` stays "what the pane should hold" for the A5 re-assert:
    // here, the position the user already has.
    // 🪤 Recognised from the CHILD ROWS THEMSELVES (which ones exist), not from
    // a "the user just clicked" flag: the click and the tasks arriving are two
    // renders, and a flag is spent by the first.
    const childRows = flatRows.filter(isChildRow).map((r) => r.key).join(',');
    const geometryOnly = !!prev && prev.selectedGagId === selectedGagId && prev.childRows !== childRows;
    if (geometryOnly) {
      const held = timelineRef.current ? timelineRef.current.scrollTop : prev.scrollTop;
      lastAppliedWindowRef.current = {
        selectedGagId, scrollTop: held, target: rowScrollTop, height: win.height, childRows,
      };
      return;
    }
    const windowChanged = !prev
      || prev.selectedGagId !== selectedGagId
      || prev.target !== rowScrollTop
      || prev.height !== win.height;
    // timelineRef.scrollTop is the single source the labels column mirrors
    // via its own onScroll handler — do not add a second sync path.
    // 🚨 ASSIGNED RAW, AND NOT THROUGH `rowTopToScrollTop`. That helper puts a
    // row at the top of the CHART pane; the focus window is read in the LABEL
    // pane, whose box starts HEADER_HEIGHT lower, so the conversion for THIS
    // question is the identity. Using it put the selected gag at the top of
    // the window instead of its middle — the regression the owner caught on
    // 2026-09-22, measured in a browser. Full derivation at
    // `focusRowScrollTop`'s docblock. 🚫 The helper is UNCHANGED and still
    // correct for its own two callers; do not "fix" it here.
    if (windowChanged && timelineRef.current) {
      timelineRef.current.scrollTop = rowScrollTop;
    }
    lastAppliedWindowRef.current = {
      selectedGagId, scrollTop: rowScrollTop, target: rowScrollTop, height: win.height, childRows,
    };
  }, [selectedGagId, flatRows]);

  // ── A5: RE-ASSERT THE SCROLL ONCE THE SHRINK HAS LANDED ─────────────────
  //
  // 🚨 THE CODE ABOVE IS RIGHT AND THE MOMENT IT RUNS IS WRONG, which is a
  // different defect from A4 and was found from the owner's own diagnosis:
  // *"its centering before the collpase so its in the wrong place after the
  // collapse"*. The ARITHMETIC already reasons against the TARGET height
  // (`focusRowScrollTop` derives it from `clampFocusedMaxHeight`) — what does
  // not is the BROWSER, which clamps an assigned `scrollTop` to
  // `scrollHeight − clientHeight` using the height the element has RIGHT NOW,
  // i.e. the un-shrunk 600px one.
  //
  // MEASURED in Chromium on a replica: 41 rows (content 1770px), select the
  // 39th, target 1596 → the write lands as **1170** (`1770 − 600`), and the
  // shrink to 174 does NOT restore it. The clicked gag ends up 426px BELOW
  // the window. It bites any selection inside the last ~13 rows, and it
  // surfaces the opposite way round from A4 (below the window, not at its
  // top), which is why it is not the symptom the owner was looking at.
  //
  // 🚫 A `useLayoutEffect` keyed on `focusedMaxHeight` does NOT fix it, and
  // that was measured rather than assumed: right after the style commit
  // `clientHeight` is still 600, because `transition-[max-height]` has not
  // begun interpolating, so the write clamps to 1170 again. 🚫 And a
  // `setTimeout` matched to the transition duration couples correctness to an
  // animation constant. So the re-assert hangs off the transition's own end.
  //
  // 🪤 IT MUST DEGRADE TO TODAY'S BEHAVIOUR, NEVER TO NOTHING. The write
  // above still happens immediately, so a browser that never fires
  // `transitionend` (reduced motion, an interrupted transition, a height that
  // did not change) keeps exactly the behaviour it has now — this only ever
  // repairs a clamped value. One listener for the component's life, so rapid
  // re-selections cannot stack or leak; it reads `lastAppliedWindowRef`,
  // which the focus effect NULLS on deselect, so the expand back to 600px
  // re-asserts nothing and the "deselect leaves scrollTop alone" ruling
  // stands.
  useEffect(() => {
    const body = chartBodyEl;
    if (!body) return undefined;
    const reassert = (e) => {
      // A plain `Event` (jsdom, and some interrupted transitions) carries no
      // propertyName; only a NAMED property other than ours is ignored.
      if (e.propertyName && e.propertyName !== 'max-height') return;
      const applied = lastAppliedWindowRef.current;
      const el = timelineRef.current;
      if (!applied || !el) return;
      if (el.scrollTop !== applied.scrollTop) el.scrollTop = applied.scrollTop;
    };
    body.addEventListener('transitionend', reassert);
    return () => body.removeEventListener('transitionend', reassert);
  }, [chartBodyEl]);

  // ── B4: on mount, scroll VERTICALLY to the closest shoot date ─────────
  //
  // Owner: "it should scroll down to the closest shoot date if one exists.
  // otherwise stay at the top", positioned about a THIRD of the way down
  // rather than flush at the top — mirroring the horizontal behaviour below
  // (`scrollLeft = todayPos - clientWidth / 3`), so what has just finished is
  // visible above and what is coming is below.
  //
  // 🚨 IT MUST NOT FIGHT THE FOCUS EFFECT, and there are two guards, not one.
  // (1) It runs ONCE — the ref latches on the first render with rows, so no
  // later refetch, filter change or re-sort can yank the user's scroll.
  // (2) It SKIPS ENTIRELY when a gag is already selected (a `?gag=` deep
  // link), because the focus effect above owns the scroll in that case and
  // ran first. The latch is set either way, so selecting a gag afterwards
  // wins permanently — which is the required behaviour: this is a MOUNT
  // behaviour, and an explicit selection always beats it.
  //
  // 🪤 It deliberately does NOT touch `lastAppliedWindowRef`. That ref
  // records what the FOCUS effect last applied; writing a mount scroll into
  // it would make the first real selection look like "no change" and skip
  // its own scroll.
  const mountScrollDone = useRef(false);
  useEffect(() => {
    if (mountScrollDone.current) return;
    if (loading || flatRows.length === 0) return;
    mountScrollDone.current = true;
    if (selectedGagId != null) return; // the focus effect owns the scroll

    const rowTop = closestShootRowTop(flatRows, toDateStr(new Date()));
    if (rowTop == null) return; // no gag has a shoot date — stay at the top
    const el = timelineRef.current;
    if (!el) return;
    // A third of the viewport above it, floored at the first row. Uses the
    // same ROW-SPACE → SCROLL-SPACE conversion as everything else.
    // 🔎 A6 note, no change made: with the date axis now pinned, the visible
    // ROW band is `clientHeight − HEADER_HEIGHT`, so this lands the closest
    // shoot date at ~27.5% of it rather than ~33%. Still "about a third", and
    // `rowTopToScrollTop` is correct for this caller either way — the axis
    // genuinely is inside this scroller's content. Left alone deliberately.
    el.scrollTop = rowTopToScrollTop(rowTop - el.clientHeight / 3);
  }, [flatRows, loading, selectedGagId]);

  // ── Axis range — min/max over windows + shoot dates, pad, snap Monday ──
  const { timelineStart, totalDays } = useMemo(() => {
    let minDate = null; let maxDate = null;
    for (const g of filtered) {
      for (const s of [g.window_start, g.shoot_date]) {
        const d = parseDate(s);
        if (d && (!minDate || d < minDate)) minDate = d;
      }
      for (const s of [g.window_end, g.shoot_date]) {
        const d = parseDate(s);
        if (d && (!maxDate || d > maxDate)) maxDate = d;
      }
      // H2: an OPEN gag's tasks must be reachable on the axis. Its window
      // already spans them — except a CANCELLED task, which the server leaves
      // out of the window but the chart still draws.
      const tasks = expanded[g.id] ? entryTasks(details[g.id]) : null;
      for (const t of tasks || []) {
        for (const d of [parseDate(t.start_date), parseDate(t.due_date)]) {
          if (d && (!minDate || d < minDate)) minDate = d;
          if (d && (!maxDate || d > maxDate)) maxDate = d;
        }
      }
    }
    if (!minDate) {
      const now = new Date(); now.setHours(0, 0, 0, 0);
      return { timelineStart: now, totalDays: 30 };
    }
    const start = addDays(minDate, -7);
    const dow = start.getDay();
    if (dow !== 1) start.setDate(start.getDate() - ((dow + 6) % 7)); // snap to Monday
    const end = addDays(maxDate, 14);
    return { timelineStart: start, totalDays: Math.max(daysDiff(start, end), 30) };
  }, [filtered, expanded, details]);

  const todayOffset = useMemo(() => {
    const t = new Date(); t.setHours(0, 0, 0, 0);
    return daysDiff(timelineStart, t);
  }, [timelineStart]);

  // ── Horizontal scroll: TODAY a third of the way in — on the first paint of
  // the chart, on a zoom change and on another department. NOT after a silent
  // refetch.
  // 🔻 Until 2026-10-12 this effect ran on every `rows` identity, so EVERY
  // silent refetch — i.e. every saved drag — snapped the chart back to today.
  // Harmless for a shoot diamond near today; with tasks draggable weeks away
  // it threw the bar the user had just moved off screen.
  // 🔑 When a save moves the START of the axis (a date dragged earlier than
  // everything else re-ranges it), the content shifts under the viewport, so
  // the scroll is moved by the same number of days: the dates the user was
  // looking at stay where they were.
  // 🪤 `rows` stays a dependency for one reason: the scroller mounts behind
  // the loading/empty early returns, so the effect must re-run when data
  // lands. A NEW scroller element counts as a first paint.
  const hScroll = useRef(null); // { el, startMs, dayWidth, deptId }
  useEffect(() => {
    const el = timelineRef.current;
    if (!el) return;
    const prev = hScroll.current;
    const startMs = timelineStart.getTime();
    // A ZOOM change on the same chart keeps the date in the middle of the view
    // (owner 2026-10-13); first paint and another department still go to today.
    const zoomOnly = prev && prev.el === el && prev.deptId === activeDeptId && prev.dayWidth !== dayWidth;
    if (zoomOnly && applyCentre()) {
      // placed by the shared rule
    } else if (!prev || prev.el !== el || prev.dayWidth !== dayWidth || prev.deptId !== activeDeptId) {
      el.scrollLeft = Math.max(0, todayOffset * dayWidth - el.clientWidth / 3);
    } else if (prev.startMs !== startMs) {
      const shiftDays = daysDiff(timelineStart, new Date(prev.startMs));
      el.scrollLeft = Math.max(0, el.scrollLeft + shiftDays * dayWidth);
    }
    hScroll.current = { el, startMs, dayWidth, deptId: activeDeptId };
  }, [todayOffset, dayWidth, rows, timelineStart, activeDeptId, applyCentre]);

  // ── Date headers (shared axis builder — utils/ganttDates.js) ──
  const dateHeaders = useMemo(() => buildDateHeaders(zoom, timelineStart, totalDays), [timelineStart, totalDays, zoom]);

  const totalWidth = totalDays * dayWidth;

  // ── Shoot-diamond drag (the ONLY drag — windows are derived, bars fixed) ──
  // 🔑 `can_edit` is SERVER-COMPUTED per row (`GET /api/gags/timeline`,
  // `canEditGag`) — strict `=== true`, so a payload without it fails CLOSED.
  // The private rule that stood here lacked the creator-while-unowned arm.
  const canDragGag = useCallback((g) => g.can_edit === true, []);

  // The diamond as the shared hook's milestone: ONE drag arithmetic for every
  // gag date (`teams/CrossTeamGanttView.js#useGanttMilestoneDrag`). `key` tells
  // one gag's marker from another's — every row carries the same field.
  const shootMilestone = (g) => ({
    key: `${g.id}:shoot_date`, gag: g, field: 'shoot_date', label: 'Shoot', date: g.shoot_date,
    liveKey: ganttGagDateLiveKey(g.id, 'shoot_date'),
  });
  const {
    msDrag: drag, msConfirm: confirmMove, setMsConfirm: setConfirmMove, startMilestoneDrag,
    milestoneDisplayDate, isMilestoneDragging, isMilestonePending,
  } = useGanttMilestoneDrag({
    timelineRef, dayWidth, timelineStart, totalDays, dragEndTime,
    onStart: () => setTooltip(null),
    keyOf: milestoneKey,
  });

  const {
    dragState, confirmDialog: confirmTask, setConfirmDialog: setConfirmTask, startTaskDrag, remoteTaskDates,
  } = useGanttTaskDrag({
    timelineRef, dayWidth, timelineStart, dragEndTime,
    onStart: () => setTooltip(null),
  });

  // ── THE TWO DRAG GATES — both the SERVER's answer, checked again at the
  // press (an element that should not drag is given no handler; this is the
  // second lock, not the only one).
  //   a gag DATE (shoot diamond, lane marker): the timeline row's `can_edit`
  //   a TASK: the gag detail's `can_edit_tasks`, through the gag panel's own
  //   gate (`gagTaskPlanning.js#gagTaskDragAllowed`). No detail yet → no drag.
  const handleMilestoneDragStart = (e, milestone) => {
    if (!canDragGag(milestone.gag)) return;
    startMilestoneDrag(e, milestone);
  };
  const handleDragStart = (e, gag) => handleMilestoneDragStart(e, shootMilestone(gag));

  const canDragTasksOf = (g) => gagTaskDragAllowed(details[g.id]?.detail, userId);
  const handleTaskDragStart = (e, g, task, handle) => {
    if (!canDragTasksOf(g)) return;
    startTaskDrag(e, task, handle);
  };

  // ── Saving a confirmed move. ONE shape for both kinds: send the request,
  // and on anything but a stored save say so (toast) — the caller's `settle`
  // then clears the pending ghost and re-reads, so the chart never keeps
  // showing a date the server did not store.
  const commitMove = async ({ request, failed, onSaved, settle }) => {
    if (confirmBusy) return;
    setConfirmBusy(true);
    try {
      const res = await request();
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || failed);
      } else {
        onSaved();
        onChanged?.(); // the page: the Table's columns and the open panel
      }
    } catch {
      toast.error(`${failed} — check your connection.`);
    }
    setConfirmBusy(false);
    settle();
  };

  const handleConfirmMove = () => {
    if (!confirmMove) return;
    const { milestone, newDate } = confirmMove;
    commitMove({
      request: () => moveGagDate(milestone.gag.id, milestone.field, newDate),
      failed: `Failed to move the ${milestone.label.toLowerCase()} date`,
      // Optimistically move the marker so it doesn't snap back to the old
      // date between confirm and the silent refetch landing below.
      onSaved: () => setRows((prev) => prev.map((r) => (
        r.id === milestone.gag.id ? { ...r, [milestone.field]: newDate } : r
      ))),
      settle: () => {
        setConfirmMove(null);
        fetchRows(true); // saved value in, or ghost reverted on failure
      },
    });
  };

  const handleConfirmTask = () => {
    if (!confirmTask) return;
    const { task, newStart, newDue } = confirmTask;
    const gagId = gagIdOfTask(details, task.id);
    if (gagId == null) { setConfirmTask(null); return; }
    commitMove({
      request: () => rescheduleGagTask(gagId, task.id, newStart, newDue),
      failed: 'Failed to reschedule task',
      // Same optimism as a marker: hold the bar on the dates just stored
      // until the re-read below confirms them.
      onSaved: () => setDetails((prev) => {
        const entry = prev[gagId];
        const tasks = entryTasks(entry);
        if (!tasks) return prev;
        const moved = tasks.map((t) => (t.id === task.id ? { ...t, start_date: newStart, due_date: newDue } : t));
        return { ...prev, [gagId]: { ...entry, detail: { ...entry.detail, tasks: moved } } };
      }),
      settle: () => {
        setConfirmTask(null);
        fetchDetail(gagId); // the stored dates — or the bar back where the server has it
        fetchRows(true); // the parent bar is derived server-side from these dates
      },
    });
  };

  // A task's live dates: dragging > pending-confirm ghost > the panel's live
  // drag of it (`gantt/ganttLiveDrag`) > stored.
  const taskLiveDates = (t) => {
    if (dragState && dragState.task.id === t.id) return { start: dragState.currentStart, due: dragState.currentDue, moving: true };
    if (confirmTask && confirmTask.task.id === t.id) return { start: confirmTask.newStart, due: confirmTask.newDue, moving: true };
    const remote = remoteTaskDates(t);
    if (remote) return { start: remote.start, due: remote.due, moving: true };
    return { start: t.start_date, due: t.due_date, moving: false };
  };

  // The pinned date axis's bottom edge: the drag card never sits above it.
  const dragCardMinTop = useCallback(
    () => (timelineRef.current ? timelineRef.current.getBoundingClientRect().top + HEADER_HEIGHT : 0),
    [],
  );
  // Live diamond position: dragging > pending-confirm ghost > saved date.
  const displayShootDate = (g) => milestoneDisplayDate(shootMilestone(g));

  // Where the current press began, in CLIENT coordinates, recorded in the
  // CAPTURE phase on the chart pane so it is set before anything else runs.
  // Only the two CHART click targets consult it; the LABEL column passes no
  // event, so a label click is never judged against a press made on the chart.
  const pressAt = useRef(null);
  const notePress = (e) => { pressAt.current = { x: e.clientX, y: e.clientY }; };
  const movedSincePress = (e) => {
    const p = pressAt.current;
    if (!p) return false;
    return Math.abs(e.clientX - p.x) > PAN_CLICK_SUPPRESS_PX
      || Math.abs(e.clientY - p.y) > PAN_CLICK_SUPPRESS_PX;
  };

  const openGag = (g, e) => {
    if (Date.now() - dragEndTime.current < 500) return; // drag release, not a click
    // A pan that began on this element: the browser still fires a click on
    // release. See PAN_CLICK_SUPPRESS_PX.
    if (e && movedSincePress(e)) return;
    onOpenGag?.(g);
  };
  // A click on the gag's OWN row (title cell, bar, diamond) opens BOTH views of
  // it: its task rows in the chart and its panel below (owner 2026-10-12: "we
  // get the tasks on the gantt chart AND the details below"). A second click on
  // a gag that has both closes both. Several gags can have rows open but only
  // one has the panel, so a click on an expanded gag WITHOUT the panel moves
  // the panel to it and keeps its rows. The chevron is the same click (owner
  // 2026-10-13: "clicking the chevron should also open up the details").
  // Same drag/pan guards as openGag; the page asks first if the panel is dirty.
  const toggleFromClick = (g, e) => {
    if (Date.now() - dragEndTime.current < 500) return;
    if (e && movedSincePress(e)) return;
    const rowsOpen = !!expanded[g.id];
    const panelOpen = selectedGagId === g.id;
    if (rowsOpen === panelOpen) toggleExpand(g);
    else if (!rowsOpen) toggleExpand(g);
    if (rowsOpen === panelOpen || !panelOpen) onOpenGag?.(g);
  };
  // A click on a CHILD row opens its gag's panel and never closes it (the page
  // toggles the open gag shut on a second click — right for the gag's own
  // row, wrong for one of its tasks).
  const openGagFromChild = (g, e) => {
    if (selectedGagId === g.id) return;
    openGag(g, e);
  };

  // ── Tooltip (read-only hover card) ──
  const showTip = (e, gag) => {
    if (drag || dragState) return;
    setTooltip({ gag, x: e.clientX + 12, y: e.clientY - 10 });
  };
  const hideTip = () => setTooltip(null);

  if (loading) return <LoadingSpinner message="Loading timeline..." />;
  if (error) return <ErrorMessage error={error} onRetry={() => fetchRows()} />;

  if (filtered.length === 0) {
    return (
      <EmptyState
        // 🔻 COPY CORRECTED WITH B2 (2026-09-22). This read "Gags appear
        // here once they have dates" and "only gags with at least one date
        // can appear on the timeline" — both were true of the server's old
        // exclusion clause and both became FALSE the moment it was deleted.
        // Every live gag in the department now appears; a gag with no shoot
        // date carries a pill instead of a diamond. Shipping a change that
        // falsifies the copy beside it is how a page ends up lying about
        // itself.
        title={rows.length === 0 ? 'No gags in this department yet' : 'No gags match this search and tab'}
        description={
          rows.length === 0
            ? 'Every gag appears here — a shoot date shows as a diamond; access, prep, strike and task dates build the window bar. Gags with no shoot date are listed at the top.'
            : 'Change the search or pick another status tab to bring gags back into the timeline.'
        }
      />
    );
  }

  return (
    /* 🧱 `isolate` — THE CHART IS ITS OWN STACKING CONTEXT (2026-09-23). The
       sticky date axis is `z-30` and the today marker `z-20`; without a
       context here both compete at PAGE level and, measured on a real render
       at 1440, painted over the Toolbar (z-20) and the app bar (`Layout.js`,
       z-30) when the page scrolled the chart under them. */
    <div data-testid="gag-timeline-root" className="isolate bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Zoom + legend */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-2">
            <GanttZoomControl zoom={zoom} onZoom={zoomTo} />
          </div>
          {/* Grouping: an `Arrange ▾` dropdown since 2026-10-13 (owner: "change
              'All (shoot order) / By sub-department' to a drop down either
              'view' or 'arrange'"); "Arrange", because "View" is the
              Table/Timeline/Labour tabs. "All (shoot order)" is FIRST and is
              the default (owner ruling 2026-09-22); nothing here is persisted.
              🧱 `portal`: the chart root is `overflow-hidden`, which would
              clip an in-flow panel. */}
          <Dropdown
            label={<span>Arrange<span className="text-gray-400 dark:text-gray-400"> · </span>{(ARRANGE_MODES.find((m) => m.id === groupMode) || ARRANGE_MODES[0]).short}</span>}
            title="Arrange the gags"
            ariaLabel="Arrange the gags"
            triggerTestId="gag-timeline-arrange"
            triggerClassName="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            panelClassName="p-1 min-w-[12rem]"
            portal
          >
            {({ close }) => (
              <div role="menu" aria-label="Arrange the gags">
                {ARRANGE_MODES.map((m) => (
                  <button
                    key={m.id} type="button" role="menuitemradio"
                    aria-checked={groupMode === m.id}
                    data-testid={`gag-timeline-arrange-${m.id}`}
                    onClick={() => { setGroupMode(m.id); close(); }}
                    className={`flex w-full items-center gap-2 px-3 py-2 min-h-[44px] md:min-h-0 rounded-md text-left text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${
                      groupMode === m.id
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 font-semibold'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span aria-hidden="true" className="w-3 text-xs">{groupMode === m.id ? '✓' : ''}</span>
                    {m.label}
                  </button>
                ))}
              </div>
            )}
          </Dropdown>
        </div>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs">
          {GAG_STATUSES.map((s) => (
            <div key={s} className="flex items-center gap-1">
              <span className="w-3 h-3 rounded" style={{ backgroundColor: GAG_STATUS_COLORS_HEX[s] }} />
              <span className="text-gray-500 dark:text-gray-400">{GAG_STATUS_LABELS[s]}</span>
            </div>
          ))}
          <GanttMilestoneLegend isHidden={isMsHidden} onToggle={toggleMs} testIdPrefix="gag-timeline-legend" />
        </div>
      </div>

      {/* Chart body — maxHeight shrinks to a 3-gag focus window while a gag is
          selected (see the useEffect above); eases via the transition rather
          than snapping. 600px is the ceiling either way — focus only ever
          shrinks the chart, never grows it. */}
      <div
        ref={setChartBodyEl}
        data-testid="gag-timeline-chart-body"
        className="flex overflow-hidden transition-[max-height] duration-300 ease-in-out"
        style={{ maxHeight: focusedMaxHeight != null ? `${focusedMaxHeight}px` : '600px' }}
      >
        {/* Fixed label column */}
        <div
          className="flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 overflow-hidden flex flex-col"
          style={{ width: LABEL_WIDTH }}
        >
          {/* B3 — see gagHeaderSummaryTitle's docblock above. An info block,
              not a single label: up to two exception lines in fixed
              priority order, each hidden at zero — no total line since
              item 3 (2026-09-22), so nothing here can disagree with the
              app bar's own gag count again. The `title` + trailing
              `sr-only` span carry the FULL sentence — including any third
              exception folded out of the visible lines — so nothing
              non-zero is ever silently dropped. Renders nothing at all
              when every count is zero. */}
          <div
            data-testid="gag-timeline-header-info"
            title={headerSummaryTitle}
            className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-3 flex flex-col justify-center gap-0.5 overflow-hidden"
            style={{ height: HEADER_HEIGHT }}
          >
            {visibleHeaderExceptions.map((ex) => (
              <span
                key={ex.key}
                data-testid={ex.testId}
                className="text-xs leading-4 text-gray-500 dark:text-gray-400 truncate"
              >
                {ex.text}
              </span>
            ))}
            {headerSummaryTitle && <span className="sr-only">{headerSummaryTitle}</span>}
          </div>
          <div id="gag-timeline-labels" className="flex-1 overflow-hidden">
            {flatRows.map((row) => {
              if (row.type === 'group') {
                return (
                  <div
                    key={`g-${row.key}`}
                    data-testid="gag-timeline-group-header"
                    // Sticky (owner-approved, spreadsheet freeze-pane
                    // behaviour): the CURRENT group's header stays pinned to
                    // the top of the scroll region while its rows scroll
                    // underneath, replacing the deleted focusWindow header
                    // clause — group context is now a permanent overlay,
                    // never spent out of the 3-gag focus budget. `top-0`
                    // resolves against `#gag-timeline-labels`'
                    // `overflow-hidden` (a valid sticky containing block —
                    // `overflow: hidden` creates one same as `auto`/`scroll`
                    // — even though its own scrollTop is driven
                    // programmatically rather than by user scroll input).
                    // OPAQUE background (no /70, /60 — a sticky row needs
                    // full opacity or the rows scrolling underneath show
                    // through) reusing this row's OWN existing tint, not a
                    // new colour. z-10 keeps it above ordinary row content.
                    // 🚫 Unverified in this test suite — jsdom has no
                    // layout, no scrolling and no sticky resolution; see the
                    // component test file for what is and isn't proven here.
                    className="sticky top-0 z-10 flex items-center px-2 gap-2 border-b border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 font-semibold bg-gray-100 dark:bg-gray-800"
                    style={{ height: GROUP_HEIGHT }}
                    onClick={() => toggleGroup(row.key)}
                  >
                    <span className={`text-gray-400 text-xs transition-transform ${collapsed[row.key] ? '' : 'rotate-90'}`}>&#9654;</span>
                    <span className="text-xs text-gray-800 dark:text-gray-200 truncate">{row.label}</span>
                    <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">{row.count}</span>
                  </div>
                );
              }
              const g = row.gag;
              // Child rows indent to sit under the gag's TITLE (past its chevron).
              const childPad = groupMode === 'all' ? 'pl-9' : 'pl-[3.25rem]';
              if (row.type === 'lane') {
                return (
                  <div
                    key={row.key}
                    data-testid={`gag-timeline-lane${row.second ? '2' : ''}-label-${g.id}`}
                    className={`flex items-center ${childPad} pr-3 border-b border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400`}
                    style={{ height: CHILD_ROW_HEIGHT }}
                  >
                    {row.second ? <span className="sr-only">Milestones, continued</span> : 'Milestones'}
                  </div>
                );
              }
              if (row.type === 'task') {
                const t = row.task;
                return (
                  <div
                    key={row.key}
                    data-testid={`gag-timeline-task-label-${t.id}`}
                    className={`flex items-center gap-1.5 ${childPad} pr-3 border-b border-gray-100 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-300 min-w-0 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/30`}
                    style={{ height: CHILD_ROW_HEIGHT }}
                    onClick={() => openGagFromChild(g)}
                    title={t.title}
                  >
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: taskBarColor(t) }} aria-hidden="true" />
                    <span className="truncate">{t.title}</span>
                    {/* Nobody is on it: said in words here, a red border on the bar. */}
                    {isUnassignedTask(t) && (
                      <span data-testid={`gag-timeline-task-unassigned-${t.id}`} className="flex-shrink-0 ml-auto text-[11px] font-medium text-red-600 dark:text-red-400 whitespace-nowrap">Unassigned</span>
                    )}
                  </div>
                );
              }
              if (row.type === 'note') {
                return (
                  <div
                    key={row.key}
                    data-testid={`gag-timeline-note-${g.id}`}
                    className={`flex items-center gap-2 ${childPad} pr-3 border-b border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400`}
                    style={{ height: CHILD_ROW_HEIGHT }}
                  >
                    <span className="truncate">{CHILD_NOTE_TEXT[row.kind]}</span>
                    {row.kind === 'error' && (
                      <button
                        type="button"
                        data-testid={`gag-timeline-retry-${g.id}`}
                        onClick={() => fetchDetail(g.id)}
                        className="flex-shrink-0 font-medium text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Retry
                      </button>
                    )}
                  </div>
                );
              }
              const isOpen = !!expanded[g.id];
              const undated = isOpen ? undatedTaskCount(details[g.id]) : 0;
              return (
                <div
                  key={`l-${g.id}`}
                  data-testid={`gag-timeline-label-${g.id}`}
                  className={`flex items-center gap-1 ${groupMode === 'all' ? 'pl-1.5' : 'pl-5'} pr-3 border-b border-gray-100 dark:border-gray-700 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 ${
                    selectedGagId === g.id
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 font-medium'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                  style={{ height: ROW_HEIGHT }}
                  onClick={() => toggleFromClick(g)}
                  title={g.title}
                >
                  {/* Expand: the Table's own row chevron (`ExpandChevron`), in its
                      own button, for the keyboard and its `aria-expanded`: the SAME click as the cell (rows + panel).
                      A sibling of the title, never around it. */}
                  <button
                    type="button"
                    data-testid={`gag-timeline-expand-${g.id}`}
                    aria-expanded={isOpen}
                    aria-label={`${isOpen ? 'Hide' : 'Show'} tasks and details for ${g.title}`}
                    title={isOpen ? 'Hide tasks and details' : 'Show tasks and details'}
                    onClick={(e) => { e.stopPropagation(); toggleFromClick(g); }}
                    className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  >
                    <ExpandChevron expanded={isOpen} />
                  </button>
                  <span className="truncate min-w-0 flex-1">{g.title}</span>
                  {/* Nobody owns this gag: said in words here, the red ring on its bar. */}
                  {needsOwner(g) && (
                    <span data-testid={`gag-timeline-no-owner-${g.id}`} className="flex-shrink-0 text-[11px] font-medium text-red-600 dark:text-red-400 whitespace-nowrap">No owner</span>
                  )}
                  {/* H4: tasks with no dates have no row. Said quietly, in words
                      (a `title` alone reaches nobody who never hovers). */}
                  {undated > 0 && (
                    <span
                      data-testid={`gag-timeline-undated-${g.id}`}
                      title={undatedTasksTitle(undated)}
                      className="flex-shrink-0 text-xs font-normal text-gray-500 dark:text-gray-400 whitespace-nowrap"
                    >
                      {undated} not dated
                      <span className="sr-only"> {undatedTasksTitle(undated)}</span>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Scrollable timeline */}
        <div
          data-testid="gag-timeline-scroll"
          className="flex-1 overflow-auto cursor-grab" ref={timelineRef}
          // Capture phase, so the press position is recorded whatever the
          // target is and before any handler on it runs. See `openGag`.
          onMouseDownCapture={notePress}
          // 🚨 A BARE 1:1 COPY, AND IT MUST STAY ONE. Subtracting HEADER_HEIGHT
          // here is a defect that shipped on 2026-09-22 and was reverted the
          // same day: the axis sits inside THIS scroller (a CONTENT offset)
          // and the label column's heading sits above ITS scroller (a BOX
          // offset), so the two 48s cancel. Proof, and the lesson, at
          // `rowTopToScrollTop`'s docblock. 🔑 `useDragScroll` writes the same
          // property on the pan path and MUST write the same number — see the
          // hook call above.
          // 🔎 THE "SLIGHT RESIDUAL OFFSET AT THE VERY BOTTOM ONLY" the owner
          // reported and ruled NOT WORTH FIXING (2026-09-22) — cause recorded
          // here, deliberately unfixed. This pane has a HORIZONTAL scrollbar
          // and the label pane does not, so its clientHeight is ~15px smaller
          // and its maximum scrollTop is ~15px LARGER. The mirror is exact
          // everywhere except the last ~15px, where the label pane has run out
          // of range and clamps while this one keeps going. Not the 48px
          // affair — that one is uniform at every position, and this is zero
          // until the very end. Fixing it means either giving the label pane
          // a matching scrollbar gutter or bounding this pane's scroll, both
          // of which cost more than the symptom.
          onScroll={(e) => { const labels = document.getElementById('gag-timeline-labels'); if (labels) labels.scrollTop = e.target.scrollTop; }}
        >
          <div style={{ width: totalWidth, minWidth: '100%' }}>
            {/* ── A6: THE DATE AXIS IS STICKY ───────────────────────────────
                Owner, shown the cost and accepting it: "date row on the
                gannt chart needs stick so that when we scroll down we still
                can see the dates".

                🔑 THE COST HE ACCEPTED IS NOT BEING CHARGED. The order
                table's version (see STICKY COLUMN HEADERS in
                `client/src/pages/CLAUDE.md`) had to change the SCROLL MODEL
                — give the scrollport a maxHeight so it owns both axes — and
                the product-visible price was the list becoming an inner
                scroll region. This pane is ALREADY that: `overflow-auto`
                plus a maxHeight from the chart body (600px at rest, the
                focus clamp while a gag is selected). Only the declaration
                changes.

                🚨 `top-0`, NEVER `top-[48px]`. Pinning a header at its own
                height is the bug that shipped on the order table first: it
                parks the header partway down the box, over the first rows,
                at rest. 🚫 And `overflow-y: clip` is not a route to pinning
                this to the PAGE — the element is already a scroll container
                by virtue of its x-axis, which it must keep or the whole page
                scrolls sideways. That was tried there and shipped worse.

                🔑 `z-30` because it must out-rank BOTH the today marker
                (z-20) and the sticky group-header strips (z-10), and OPAQUE
                because rows scroll underneath it.

                🔑 THE FOCUS WINDOW AND THIS ARE ONE GEOMETRY FROM TWO ENDS.
                `clampFocusedMaxHeight` already reserves HEADER_HEIGHT for
                this row, and until the A4 fix the focus scroll spent that
                reservation on a row instead — pinning the axis is what puts
                the reserved band back to the use it was reserved for.
                🚫 `position: sticky` replaces `relative`; it is still a
                positioned ancestor, so the absolutely-positioned date cells
                below are unaffected. Do not re-add `relative`.
                🚫 jsdom pins nothing — whether this holds on screen is a
                BROWSER check; §A6 in the test file says so. */}
            <GanttDateAxis
              testId="gag-timeline-date-axis"
              className={`sticky top-0 ${GANTT_AXIS_Z}`}
              dateHeaders={dateHeaders}
              dayWidth={dayWidth}
              height={HEADER_HEIGHT}
            />

            {/* Rows */}
            <div className="relative">
              {/* Background grid */}
              <GanttGrid dateHeaders={dateHeaders} dayWidth={dayWidth} height={totalRowHeight} />
              <GanttTodayLine todayOffset={todayOffset} totalDays={totalDays} dayWidth={dayWidth} height={totalRowHeight} />

              {flatRows.map((row) => {
                if (row.type === 'group') {
                  // Sticky in this pane too, so the two panes scroll as one
                  // visual unit — see the label column's group header above
                  // for the full reasoning (sticky containing block,
                  // opacity, z-index). This canvas strip carries no text of
                  // its own (the label pane owns the group name), only the
                  // tint, kept in step with it.
                  return (
                    <div
                      key={`g-${row.key}`}
                      data-testid="gag-timeline-group-header-canvas"
                      // 🪤 A6: PINS AT HEADER_HEIGHT HERE AND AT 0 ON THE
                      // LABEL SIDE, AND THE ASYMMETRY IS REQUIRED. The two
                      // panes' sticky frames differ by exactly that: the date
                      // axis is INSIDE this scrollport (a CONTENT offset)
                      // while the label column's info block is OUTSIDE its
                      // own (a BOX offset). Pinning both at 0 puts this strip
                      // underneath the axis while its twin sits 48px lower —
                      // the same two 48s that cancel in the label sync,
                      // arriving from the sticky side. Measured level in
                      // Chromium; pinned by §A6 in the test file.
                      // 🚫 NOT the order table's "offset twice" bug: that
                      // offset named a band OUTSIDE the scrollport. This one
                      // names a sticky element inside the same scrollport,
                      // which is ordinary sticky stacking.
                      className="sticky z-10 border-b border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800"
                      style={{ height: GROUP_HEIGHT, top: HEADER_HEIGHT }}
                    />
                  );
                }
                const g = row.gag;
                // ── A gag's CHILD rows (expanded). Faintly tinted so they read
                // as belonging to the gag above; translucent, so the weekend
                // grid still shows through.
                const childRowClass = 'relative border-b border-gray-100 dark:border-gray-700 bg-gray-100/60 dark:bg-gray-900/40';
                if (row.type === 'note') {
                  return <div key={row.key} className={childRowClass} style={{ height: CHILD_ROW_HEIGHT }} />;
                }
                if (row.type === 'lane') {
                  const draggableMs = canDragGag(g);
                  return (
                    <div key={row.key} data-testid={`gag-timeline-lane${row.second ? '2' : ''}-${g.id}`} className={childRowClass} style={{ height: CHILD_ROW_HEIGHT }}>
                      {row.milestones.map((m) => {
                        const shown = milestoneDisplayDate(m);
                        const d = parseDate(shown);
                        if (!d) return null;
                        const centerX = daysDiff(timelineStart, d) * dayWidth + dayWidth / 2;
                        const moving = isMilestoneDragging(m);
                        return (
                          <div
                            key={m.field}
                            data-testid={`gag-timeline-ms-${g.id}-${m.field}`}
                            className={`absolute top-0 h-full flex items-center gap-1 ${MILESTONE_SHAPE_CLASSES[m.shape] || MILESTONE_SHAPE_CLASSES.diamond}`}
                            style={{
                              left: centerX - 6,
                              zIndex: moving ? GANTT_DRAG_Z : 10,
                              opacity: moving || isMilestonePending(m) ? 0.7 : 1,
                              cursor: draggableMs ? GANTT_CURSOR.move : GANTT_CURSOR.open,
                            }}
                            // Carried only while the marker owns a drag (see the
                            // diamond below for why a React-level stop is too late).
                            data-pan-skip={draggableMs ? '' : undefined}
                            {...(moving ? { [GANTT_DRAGGING_ATTR]: '' } : null)}
                            onMouseDown={draggableMs ? (e) => handleMilestoneDragStart(e, m) : undefined}
                            onTouchStart={draggableMs ? (e) => handleMilestoneDragStart(e, m) : undefined}
                            onClick={(e) => openGagFromChild(g, e)}
                            title={`${m.label}: ${formatDate(shown)}`}
                          >
                            <MilestoneShape shape={m.shape} />
                            {zoom !== 'quarter' && (
                              <span className="text-[10px] font-semibold select-none pointer-events-none whitespace-nowrap">
                                {m.label}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                }
                if (row.type === 'task') {
                  const t = row.task;
                  const live = taskLiveDates(t);
                  const span = ganttBarSpan(live.start, live.due, timelineStart, dayWidth);
                  const canDragTask = canDragTasksOf(g);
                  const isTaskDragging = !!dragState && dragState.task.id === t.id;
                  const width = span ? Math.max(span.width, 8) : 0;
                  const unassigned = isUnassignedTask(t);
                  return (
                    <div key={row.key} data-testid={`gag-timeline-task-row-${t.id}`} className={childRowClass} style={{ height: CHILD_ROW_HEIGHT }}>
                      {span && (
                        <div
                          data-testid={`gag-timeline-task-${t.id}`}
                          {...(isTaskDragging ? { [GANTT_DRAGGING_ATTR]: '' } : null)}
                          data-unassigned={unassigned ? 'true' : undefined}
                          className={`absolute rounded overflow-hidden text-white ${isTaskDragging ? 'shadow-lg' : (unassigned ? '' : 'shadow-sm hover:shadow-md')}`}
                          style={{
                            left: span.left, width, height: CHILD_BAR_HEIGHT,
                            top: (CHILD_ROW_HEIGHT - CHILD_BAR_HEIGHT) / 2,
                            ...(unassigned ? unassignedBarStyle(taskBarColor(t)) : { backgroundColor: taskBarColor(t) }),
                            opacity: live.moving ? 0.7 : (t.status === 'cancelled' ? 0.55 : 1),
                            zIndex: isTaskDragging ? GANTT_DRAG_Z : 10,
                            cursor: canDragTask ? GANTT_CURSOR.move : GANTT_CURSOR.open,
                          }}
                          // Only a bar that owns a drag blocks the pan (the gag
                          // bar's rule, above PAN_CLICK_SUPPRESS_PX).
                          data-pan-skip={canDragTask ? '' : undefined}
                          onClick={(e) => openGagFromChild(g, e)}
                          title={`${t.title} (${live.start ? formatDate(live.start) : '?'} → ${live.due ? formatDate(live.due) : '?'})${unassigned ? ' · Unassigned' : ''}`}
                        >
                          {/* The label column already names the task, so the bar
                              repeats the title only once there is room to read it
                              (a 3-day bar at Month zoom is 48px: "D…" says nothing). */}
                          {width > 56 && (
                            <span
                              className={`text-[11px] font-medium truncate block pointer-events-none select-none whitespace-nowrap ${unassigned ? 'text-gray-700 dark:text-gray-200' : 'text-white'}`}
                              style={{ lineHeight: `${CHILD_BAR_HEIGHT}px`, paddingLeft: canDragTask ? 10 : 6, paddingRight: canDragTask ? 10 : 6, textShadow: unassigned ? 'none' : '0 1px 2px rgba(0,0,0,0.3)' }}
                            >
                              {t.title}
                            </span>
                          )}
                          {canDragTask && (
                            <TaskDragZones task={t} width={width} onStart={(e, handle) => handleTaskDragStart(e, g, t, handle)} />
                          )}
                        </div>
                      )}
                    </div>
                  );
                }
                const color = GAG_STATUS_COLORS_HEX[g.status] || GAG_STATUS_COLORS_HEX.todo;
                const ws = parseDate(g.window_start);
                const we = parseDate(g.window_end);
                const bar = ws && we
                  ? { left: daysDiff(timelineStart, ws) * dayWidth, width: Math.max((daysDiff(ws, we) + 1) * dayWidth, 8) }
                  : null;
                const shootStr = displayShootDate(g);
                const shootD = parseDate(shootStr);
                const diamondX = shootD ? daysDiff(timelineStart, shootD) * dayWidth + dayWidth / 2 : null;
                const isDragging = isMilestoneDragging(shootMilestone(g));
                const isPending = isMilestonePending(shootMilestone(g));
                const draggable = !!g.shoot_date && canDragGag(g);
                // Expanded: every milestone is in the lane below, none here.
                const rowsOpen = !!expanded[g.id];
                // A marker on the bar's first day (prep usually is) would sit on
                // the title's first letters: start the title after it.
                const markerAtBarStart = !rowsOpen && !!ws && BAR_MILESTONE_DEFS.some((def) => {
                  const md = parseDate(dateOnly(g[def.field]));
                  return !!md && daysDiff(ws, md) === 0;
                });

                return (
                  <div
                    key={`r-${g.id}`}
                    className={`relative border-b border-gray-100 dark:border-gray-700 ${
                      selectedGagId === g.id ? 'bg-blue-50/60 dark:bg-blue-900/15' : ''
                    }`}
                    style={{ height: ROW_HEIGHT }}
                  >
                    {/* Window bar — derived, never draggable. Cancelled gags
                        stay visible, just greyed by their status colour. */}
                    {bar && (
                      <div
                        // An unowned gag wears the "nobody is on this" ring over its status
                        // colour, and drops the shadow classes: `html.dark .shadow-sm` forces
                        // a pale border that would hide the ring (the unassigned task's rule).
                        className={`absolute rounded-md overflow-hidden text-white cursor-pointer ${needsOwner(g) ? '' : 'shadow-sm hover:shadow-md'}`}
                        data-no-owner={needsOwner(g) ? 'true' : undefined}
                        style={{
                          left: bar.left, width: bar.width, height: BAR_HEIGHT,
                          top: (ROW_HEIGHT - BAR_HEIGHT) / 2,
                          backgroundColor: color,
                          ...(needsOwner(g) ? { boxShadow: UNASSIGNED_RING } : null),
                          opacity: g.status === 'cancelled' ? 0.55 : 1,
                          zIndex: 10,
                        }}
                        data-testid={`gag-timeline-bar-${g.id}`}
                        // 🚫 NO `data-pan-skip`. This bar owns no drag (see the
                        // comment above it), so the mark only made the chart
                        // dead to panning wherever a bar sits. Click-to-open is
                        // protected by PAN_CLICK_SUPPRESS_PX instead.
                        onClick={(e) => toggleFromClick(g, e)}
                        onMouseEnter={(e) => showTip(e, g)}
                        onMouseLeave={hideTip}
                      >
                        {bar.width > 50 && (
                          <span
                            className="text-xs text-white font-medium truncate block pointer-events-none select-none whitespace-nowrap"
                            style={{ lineHeight: `${BAR_HEIGHT}px`, paddingLeft: markerAtBarStart ? dayWidth / 2 + 12 : 8, paddingRight: 8, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                          >
                            {g.title}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Access / prep / strike ON THE BAR while collapsed. White
                        with a dark edge: they always fall inside the window
                        bar (the window is derived from them), whose fill is
                        the status colour, so the lane's own colours would
                        vanish on a like-coloured bar. Same drag as the lane. */}
                    {!rowsOpen && BAR_MILESTONE_DEFS.filter((def) => g[def.field] && !isMsHidden(def.field)).map((def) => {
                      const m = milestoneOf(g, def);
                      const shown = milestoneDisplayDate(m);
                      const d = parseDate(shown);
                      if (!d) return null;
                      const moving = isMilestoneDragging(m);
                      const canDragMs = canDragGag(g);
                      return (
                        <div
                          key={m.field}
                          data-testid={`gag-timeline-barms-${g.id}-${m.field}`}
                          className={`absolute top-0 flex items-start ${MILESTONE_SHAPE_CLASSES[m.shape] || MILESTONE_SHAPE_CLASSES.diamond}`}
                          style={{
                            left: daysDiff(timelineStart, d) * dayWidth + dayWidth / 2 - 6,
                            height: ROW_HEIGHT,
                            paddingTop: BAR_TOP - 6,
                            zIndex: moving ? GANTT_DRAG_Z : 15,
                            opacity: moving || isMilestonePending(m) ? 0.7 : 1,
                            cursor: canDragMs ? GANTT_CURSOR.move : GANTT_CURSOR.open,
                            filter: MARKER_HALO,
                          }}
                          data-pan-skip={canDragMs ? '' : undefined}
                          {...(moving ? { [GANTT_DRAGGING_ATTR]: '' } : null)}
                          onMouseDown={canDragMs ? (e) => handleMilestoneDragStart(e, m) : undefined}
                          onTouchStart={canDragMs ? (e) => handleMilestoneDragStart(e, m) : undefined}
                          onClick={(e) => toggleFromClick(g, e)}
                          title={`${m.label}: ${formatDate(shown)}`}
                        >
                          <MilestoneShape shape={m.shape} />
                          <span className="sr-only">{m.label}: {formatDate(shown)}</span>
                        </div>
                      );
                    })}

                    {/* B2: the "No shoot date" placeholder, where the DIAMOND
                        would be — not where the bar would be. A gag can have
                        no shoot date and still have a window, in which case
                        its bar is drawn above and this sits over it, which is
                        correct: the bar says when the work happens, the pill
                        says the shoot is unscheduled.
                        🪤 `sticky left-0` so it stays readable at any
                        horizontal scroll offset. The row has no intrinsic x
                        for a gag with no date, and pinning it at the canvas
                        origin would put it off screen the moment the chart
                        scrolls to today (which B4 does on mount).
                        🚫 jsdom resolves no sticky positioning and composites
                        nothing, so neither the pinning nor the contrast is
                        proven by this suite — both are owed browser checks
                        and the test file says so. */}
                    {hasNoShootDate(g) && (
                      <div
                        data-testid={`gag-timeline-no-shoot-date-${g.id}`}
                        className="sticky left-0 z-20 inline-flex items-center rounded-full border border-dashed border-gray-400 dark:border-gray-500 bg-gray-100 dark:bg-gray-700 px-2 text-[11px] font-medium text-gray-700 dark:text-gray-200 whitespace-nowrap"
                        style={{
                          height: NO_SHOOT_PILL_HEIGHT,
                          marginTop: (ROW_HEIGHT - NO_SHOOT_PILL_HEIGHT) / 2,
                        }}
                        title={NO_SHOOT_DATE_TITLE}
                      >
                        {NO_SHOOT_DATE_LABEL}
                        {/* The fuller sentence for assistive technology. NOT
                            an `aria-label` on the wrapper: that would REPLACE
                            the visible words rather than add to them. */}
                        <span className="sr-only"> {NO_SHOOT_DATE_TITLE}</span>
                      </div>
                    )}

                    {/* Shoot diamond ON THE GAG'S OWN ROW — the whole point of
                        this view. Marker-only rows (null window) render just
                        this. Draggable when the row's `can_edit` is true. */}
                    {diamondX != null && !rowsOpen && !isMsHidden('shoot_date') && (
                      <div
                        className={`absolute flex items-center justify-center ${DIAMOND_CLASS}`}
                        style={{
                          left: diamondX - DIAMOND / 2,
                          top: BAR_TOP - DIAMOND / 2,
                          width: DIAMOND, height: DIAMOND,
                          filter: MARKER_HALO,
                          zIndex: isDragging ? 30 : 20,
                          opacity: isDragging || isPending ? 0.7 : 1,
                          cursor: draggable ? GANTT_CURSOR.move : GANTT_CURSOR.open,
                        }}
                        data-testid={`gag-timeline-diamond-${g.id}`}
                        // Carried ONLY while this diamond owns the drag —
                        // `teams/CrossTeamGanttView.js`'s `draggableMs` rule.
                        // 🪤 It cannot be replaced by the `stopPropagation` in
                        // `handleDragStart`: the hook listens NATIVELY on the
                        // scroll pane, which the event reaches before React
                        // dispatches from the root, so a React-level stop is
                        // already too late.
                        data-pan-skip={draggable ? '' : undefined}
                        {...(isDragging ? { [GANTT_DRAGGING_ATTR]: '' } : null)}
                        onMouseDown={draggable ? (e) => handleDragStart(e, g) : undefined}
                        onTouchStart={draggable ? (e) => handleDragStart(e, g) : undefined}
                        onClick={(e) => toggleFromClick(g, e)}
                        onMouseEnter={(e) => showTip(e, g)}
                        onMouseLeave={hideTip}
                      >
                        <ShootDiamond />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Live dates while a task bar or a milestone is dragged — the shared
          drag card (`gantt/GanttDragDatesTooltip`); it flips below the bar
          rather than sit over the pinned date axis. */}
      <GanttDragDatesTooltip
        containerRef={timelineRef}
        getMinTop={dragCardMinTop}
        testId="gag-timeline-drag-dates"
        drag={dragState
          ? { title: dragState.task.title, start: dragState.currentStart, due: dragState.currentDue, oldStart: dragState.originalStart, oldDue: dragState.originalDue }
          : drag ? { title: `${drag.milestone.label} — ${drag.milestone.gag.title}`, date: drag.currentDate, oldDate: drag.milestone.date } : null}
      />

      {/* Hover tooltip — read-only card (click the row/bar to open the panel) */}
      {/* 🧱 PORTALLED, at the z-60 ESCAPE TIER (`components/ui/CLAUDE.md`): the
          chart root is `isolate`, so an in-tree `fixed` child can no longer beat
          the app bar or Toolbar. The shoot-date confirm below is portalled for
          the same reason — in-tree its backdrop would sit UNDER the page chrome. */}
      {tooltip && !drag && !dragState && createPortal((
        <div
          className="fixed bg-gray-900 text-white text-xs rounded-lg shadow-xl px-4 py-3 max-w-xs pointer-events-none"
          style={{ left: tooltip.x, top: tooltip.y, zIndex: 60 }}
        >
          <div className="font-semibold text-sm mb-1.5">{tooltip.gag.title}</div>
          <div className="text-gray-300 mb-1.5">{tooltip.gag.sub_department_name || 'No sub-department'}</div>
          <div className="flex items-center gap-1.5 mb-2 flex-wrap">
            <span
              className="px-1.5 py-0.5 rounded text-[10px] font-bold"
              style={{ backgroundColor: (GAG_STATUS_COLORS_HEX[tooltip.gag.status] || '#6B7280') + '55' }}
            >
              {GAG_STATUS_LABELS[tooltip.gag.status] || tooltip.gag.status}
            </span>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-white/15">
              {TASK_PRIORITY_LABELS[tooltip.gag.priority] || tooltip.gag.priority}
            </span>
            {GAG_RA_BADGES[tooltip.gag.ra_status] && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${GAG_RA_BADGES[tooltip.gag.ra_status].cls}`}>
                {GAG_RA_BADGES[tooltip.gag.ra_status].label}
              </span>
            )}
          </div>
          {tooltip.gag.window_start && (
            <div className="text-gray-300 mb-1">
              <span className="text-gray-500">Window</span> {formatDate(tooltip.gag.window_start)}
              <span className="text-gray-500 mx-1">→</span>{formatDate(tooltip.gag.window_end)}
            </div>
          )}
          {tooltip.gag.shoot_date && (
            <div className="text-gray-300 mb-1">
              <span className="text-gray-500">Shoot</span> {formatDate(tooltip.gag.shoot_date)}
            </div>
          )}
          <div className="text-gray-400">
            {tooltip.gag.owner_name || 'Unassigned'}
            {tooltip.gag.task_count > 0 && <span className="ml-2">{tooltip.gag.task_count} task{tooltip.gag.task_count === 1 ? '' : 's'}</span>}
          </div>
        </div>
      ), document.body)}

      {/* A gag DATE move (the shoot diamond or a lane marker) asks first. */}
      {confirmMove && (
        <MoveConfirmDialog
          title={`Move ${confirmMove.milestone.label} Date`}
          busy={confirmBusy}
          onCancel={() => setConfirmMove(null)}
          onConfirm={handleConfirmMove}
        >
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
            Move the {confirmMove.milestone.label.toLowerCase()} date for <strong>{confirmMove.milestone.gag.title}</strong> to{' '}
            <span className="text-blue-600 dark:text-blue-400 font-semibold">{formatDate(confirmMove.newDate)}</span>?
          </p>
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            <DateChange from={confirmMove.oldDate} to={confirmMove.newDate} />
          </div>
        </MoveConfirmDialog>
      )}

      {/* A TASK move or resize asks first — the gag panel's "Reschedule Task". */}
      {confirmTask && (
        <MoveConfirmDialog
          title="Reschedule Task"
          busy={confirmBusy}
          onCancel={() => setConfirmTask(null)}
          onConfirm={handleConfirmTask}
        >
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1"><strong>{confirmTask.task.title}</strong></p>
          <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1 mb-4">
            {confirmTask.oldStart !== confirmTask.newStart && (
              <DateChange label="Start" from={confirmTask.oldStart} to={confirmTask.newStart} />
            )}
            {confirmTask.oldDue !== confirmTask.newDue && (
              <DateChange label="Due" from={confirmTask.oldDue} to={confirmTask.newDue} />
            )}
          </div>
        </MoveConfirmDialog>
      )}
    </div>
  );
}
